import React from "react";

import moment from "moment";
import { toast as alert } from "react-toastify";
import * as Icon from "react-feather";
import Modal from "react-responsive-modal";

import BookkeepingService, { CUSTOM_PERIOD_LENGTH } from "../../../../services/BookkeepingService";
import AFSService from "../../../../services/AFSService";
import PlaidService from "../../../../services/PlaidService";
import ClientService from "../../../../services/ClientService";
import UtilityService from "../../../../services/UtilityService";

import WComponent from "../../../common/WComponent";
import Loader from "../../../common/loader/Loader";
import clsx from "clsx";

import "./bookkeeping-tool.css";
import Button from "../../../common/button/Button";
import DateSelector from "../../../common/date_selector/DateSelector";
import Checkbox from "../../../common/checkbox/Checkbox";
import ToolsSelect from "../kit/ToolsSelect";

import BookkeepingTable from "./BookkeepingTable";
import CoaMappingReview from "./CoaMappingReview";
import BankAccountReview from "./BankAccountReview";
import OpeningBalanceEntry from "./OpeningBalanceEntry";
import StatementVerificationModal from "./StatementVerificationModal";
import ImportedFilesModal from "./ImportedFilesModal";
import FiscalPeriodFields from "./FiscalPeriodFields";
import FileDropZone from "../../../common/file_drop_zone/FileDropZone";
import ToolsModal from "../kit/ToolsModal";
import { INDUSTRY_ENUM, toCanonicalIndustry } from "../../../../constants/Industries";
import ColSelect from "../../../common/col_select/ColSelect";

const emptyGlReferenceState = () => ({
	glReferenceFile: null,
	glReferenceHeaders: [],
	glReferenceRows: [],
	glReferenceColumnMap: { account: -1, accountNumber: -1, description: -1, date: -1, debit: -1, credit: -1, tax: -1 },
	glReferenceForTable: null,
	glLayout: "",
	glEntityName: "",
});

const emptyWizardImportState = () => ({
	booksCouldNotBeRead: false,
	wizardStep: 1,
	importMode: "",
	glSource: "",
	cwFiles: [],
	bankAccountsFromCw: [],
	bankAccountStatementMap: {},
	cwSkipped: false,
	statementVerificationGroups: null,
	statementAccountsOnRecord: [],
	statementsPutBackThisSession: [],
	pendingStatementDraft: null,
	verifiedStatementTransactions: [],
	verifiedStatementFileNames: [],
	sessionStatementFileHashes: [],
	statementSignByFile: {},
	statementColumnsByFile: {},
	glAccountList: [],
	glDetectedBankAccounts: [],
	glBankDetecting: false,
	glOpeningBalances: [],
	glOpeningReviewOpen: false,
	glOpeningReviewDone: false,
	confirmedBankAccounts: [],
	fileByBankAccount: {},
	currencyByBankAccount: {},
	bankReviewDraft: null,
	bankAccountsOnTheFile: [],
	statementsOnTheShelfByAccount: {},
	chartAccountsForBankPicker: [],
	bankAccountsLoading: false,
	transactions: [],
	importedFileNames: null,
	periodExclusions: null,
	...emptyGlReferenceState(),
});


const XeroGlyph = ({ className }) => (
	<svg className={className} viewBox="0 0 32 32" aria-hidden="true">
		<circle cx="16" cy="16" r="16" fill="#13B5EA" />
		<path d="M11.8 11.8 L20.2 20.2 M20.2 11.8 L11.8 20.2" stroke="#ffffff" strokeWidth="2.4" strokeLinecap="round" />
	</svg>
);

const QuickBooksGlyph = ({ className }) => (
	<svg className={className} viewBox="0 0 32 32" aria-hidden="true">
		<circle cx="16" cy="16" r="16" fill="#2CA01C" />
		<text x="16" y="21" textAnchor="middle" fontSize="13" fontWeight="700" fill="#ffffff" fontFamily="Modern Era, Arial, sans-serif">qb</text>
	</svg>
);

const CaseWareGlyph = ({ className }) => (
	<svg className={className} viewBox="0 0 32 32" aria-hidden="true">
		<rect width="32" height="32" rx="7" fill="#12447f" />
		<text x="16" y="21" textAnchor="middle" fontSize="12" fontWeight="700" fill="#ffffff" fontFamily="Modern Era, Arial, sans-serif">cw</text>
	</svg>
);

const EXCEL_GL_SOURCES = ["caseware", "xero", "quickbooks"];

const EXCEL_GL_SOURCE_LABELS = { caseware: "CaseWare", xero: "Xero", quickbooks: "QuickBooks" };

const GL_LAYOUT_LABELS = { "signed-amount": "Amount column", "debit-credit": "Debit and Credit columns" };

const fileNameEndsWith = (fileName, suffix) => fileName.toLowerCase().endsWith(suffix);

const caseWareDataFileSuffixes = ["am.dbf", "gl.dbf", "mp.dbf", "bl.dbf", "ts.dbf", "an.dbf", "fp.dbf"];
const bankIdentificationFileSuffixes = ["am.dbf", "bl.dbf", "mp.dbf", "fp.dbf"];
const requiredCaseWareFileSuffixes = ["am.dbf", "bl.dbf"];

const isCaseWareDataFile = fileName => {
	const matchesKnownSuffix = caseWareDataFileSuffixes.some(suffix => fileNameEndsWith(fileName, suffix));
	return matchesKnownSuffix;
};

const missingRequiredCaseWareFiles = cwFiles => {
	const hasFileWithSuffix = suffix => cwFiles.some(file => fileNameEndsWith(file.name, suffix));
	const missingSuffixes = requiredCaseWareFileSuffixes.filter(suffix => !hasFileWithSuffix(suffix));
	return missingSuffixes;
};

const statementFileNamesFromAccountMap = (bankAccountsFromCw, bankAccountStatementMap) => {
	const accountsWithStatement = bankAccountsFromCw.filter(account => bankAccountStatementMap[account.accountNumber]);
	const fileNames = accountsWithStatement.map(account => bankAccountStatementMap[account.accountNumber].name);
	return fileNames;
};

const formatOpeningBalance = balance => {
	if (balance == null || balance === 0) return "$0";
	const formattedBalance = UtilityService.formatDollarBalance({ balance, decimalPlaces: 2 });
	return formattedBalance;
};

class BookkeepingTool extends WComponent {
	constructor(props) {
		super(props);

		this.state = {
			...emptyWizardImportState(),
			wizardOpen: false,

			cwPriorYearEnd: null,
			bookingPeriod: 12,
			cwIdentifyLoading: false,
			coaMappingOpen: false,
			importedFilesModalOpen: false,

			connectedAccounts: [],
			endDate: new Date(),
			startDate: new Date(),
			plaidDatesTouched: false,

			wpfEngagementId: null,
			engagementContextError: "",
			transactionsBeforeImport: null,

			fiscalPeriod: null,
			periodConfirmed: false,
			periodDraftYearEnd: "",
			periodDraftLengthMonths: 12,
			periodDraftFirstDay: "",
			periodDraftError: "",

			isBootstrapFile: false,
			openingBalancesDone: false,

			loading: false,
			stillReadingTheBooks: true,

			showClearConfirm: false,

			statementsWereChecked: false,
			statementsToPutRight: [],
			statementDifferenceWasAccepted: false,
			acceptStatementsOpen: false,
			acceptStatementsReason: "",
			acceptStatementsSaving: false,

			industry: "",
			province: "",
			hasTax: true,
			gstHstAccount: null,
			sessionStartTime: null,
			loadedGlEntries: [],
	loadedBankSideAccounts: [],

			clientIndustry: null,
			clientCustomFields: [],
		};
	}

	getImportSourceFiles = () => {
		const { bankAccountStatementMap, fileByBankAccount, glReferenceFile, cwFiles } = this.state;

		const accountStatementFiles = Object.values(bankAccountStatementMap || {}).filter(Boolean);
		const bankAccountUploads = Object.values(fileByBankAccount || {}).filter(Boolean);
		const spreadsheetStatements = bankAccountUploads.map(upload => upload.file).filter(Boolean);
		const pdfStatements = bankAccountUploads.flatMap(upload => (Array.isArray(upload.files) ? upload.files : []));

		const allFiles = [
			...(glReferenceFile ? [glReferenceFile] : []),
			...accountStatementFiles,
			...spreadsheetStatements,
			...pdfStatements,
			...(cwFiles || []),
		];

		const seenKeys = new Set();
		const uniqueFiles = allFiles.filter(file => {
			const key = UtilityService.getFileKey(file);
			if (seenKeys.has(key)) return false;
			seenKeys.add(key);
			return true;
		});
		return uniqueFiles;
	};

	buildVerifiedTransactionsForTable = () => {
		const tableTransactions = this.state.verifiedStatementTransactions.map(transaction => ({
			date: transaction.date,
			description: transaction.description,
			merchant: "",
			amount: transaction.amount,
			accountName: transaction.bankAccountName,
			bankAccountNumber: null,
			selected: false,
			classification: null,
			tax: "",
			clientBankStatementId: transaction.clientBankStatementId || null,
		}));
		return tableTransactions;
	};

	collectGeneratedTransactions = async () => {
		let { clientId } = this.props;
		let { endDate, startDate, connectedAccounts, bankAccountsFromCw, bankAccountStatementMap, statementSignByFile, statementColumnsByFile, confirmedBankAccounts, fileByBankAccount, currencyByBankAccount, wpfEngagementId } = this.state;
		const statementColumnMapByFile = UtilityService.mapValues({ object: statementColumnsByFile, toValue: columns => columns.columnMap });
		const verifiedTransactions = this.buildVerifiedTransactionsForTable();
		const verifiedFileNames = this.state.verifiedStatementFileNames;

		const statementsWereAttachedToAccounts = confirmedBankAccounts.length > 0;
		if (statementsWereAttachedToAccounts) {
			const bankAccountUploads = confirmedBankAccounts
				.map(accountName => {
					const upload = fileByBankAccount[accountName];
					if (!upload || !upload.file) return null;
					return { accountName, file: upload.file, kind: upload.kind };
				})
				.filter(Boolean);

			const byAccount = await BookkeepingService.collectStatementTransactionsByAccount({
				clientId,
				wpfEngagementId,
				bankAccountUploads,
				statementSignByFile,
				statementColumnMapByFile,
				currencyByAccount: currencyByBankAccount,
			});
			const importedFileNames = [...bankAccountUploads.map(upload => upload.file.name), ...verifiedFileNames];
			return { transactions: [...byAccount.transactions, ...verifiedTransactions], invalidCount: byAccount.invalidCount, importedFileNames };
		}

		const formattedStartDate = moment(startDate).format("YYYY-MM-DD");
		const formattedEndDate = moment(endDate).format("YYYY-MM-DD");
		const { transactions, invalidCount } = await BookkeepingService.collectTransactionsForGeneration({
			clientId,
			statementColumnMapByFile,
			bankAccountsFromCw,
			bankAccountStatementMap,
			connectedAccounts,
			startDate: formattedStartDate,
			endDate: formattedEndDate,
		});
		const importedFileNames = [...statementFileNamesFromAccountMap(bankAccountsFromCw, bankAccountStatementMap), ...verifiedFileNames];
		return { transactions: [...transactions, ...verifiedTransactions], invalidCount, importedFileNames };
	};

	fileNonStatementSourcesInRegistry = async ({ transactions, importedFileNames }) => {
		const verifiedStatementRows = new Set(this.state.verifiedStatementTransactions.map(transaction => [transaction.date, transaction.description, transaction.amount].join("|")));
		const nonStatementTransactions = (transactions || []).filter(transaction => !verifiedStatementRows.has([transaction.date, transaction.description, transaction.amount].join("|")));
		if (nonStatementTransactions.length === 0) return [];

		const rowsByAccount = BookkeepingService.groupTransactionsByBankAccount({ transactions: nonStatementTransactions });
		const sourceNameForTheWholeImport = BookkeepingService.buildImportSourceLabel({ fileNames: importedFileNames });
		const source = this.state.importMode === "gl" ? "gl_import" : "csv_import";

		const registrationRows = Object.keys(rowsByAccount)
			.map(accountName => {
				const upload = this.state.fileByBankAccount[accountName];
				const sourceName = upload && upload.file ? upload.file.name : sourceNameForTheWholeImport;
				return BookkeepingService.buildNonStatementRegistrationRow({
					clientId: this.props.clientId,
					source,
					sourceName,
					accountLabel: accountName || null,
					transactions: rowsByAccount[accountName],
				});
			})
			.filter(Boolean);
		if (registrationRows.length === 0) return [];

		const result = await BookkeepingService.registerStatements({ clientId: this.props.clientId, statements: registrationRows });
		if (!result || result.error) {
			alert.warning(`These imported transactions could not be filed on the client's record (${(result && result.error) || "no response"}). Transactions held out by the fiscal-period cutoff will not be available to the neighbouring period until they are imported again.`, { autoClose: 12000 });
			return [];
		}
		return result.results || [];
	};

	rememberStatementsPutBackOnTheShelf = async ({ registrationResults }) => {
		const putBack = BookkeepingService.listStatementsPutBackOnTheShelf({ registrationResults });
		if (putBack.length === 0) return;

		const restoredCount = putBack.reduce((count, statement) => count + statement.transactionCount, 0);
		const statementsWere = putBack.length === 1 ? "statement was" : "statements were";
		const isOrAre = putBack.length === 1 ? "is" : "are";
		const restoredTransactions = UtilityService.describeCount({ count: restoredCount, singular: "transaction" });
		alert.info(`${putBack.length} ${statementsWere} removed earlier and ${isOrAre} back on the file, with ${restoredTransactions} returned to the books as they were.`, { autoClose: 10000 });

		const statementIds = putBack.map(statement => statement.statementId);
		const rememberedIds = [...new Set([...this.state.statementsPutBackThisSession, ...statementIds])];
		await this.update({ statementsPutBackThisSession: rememberedIds });
	};

	rereadTheBooksIfStatementsWerePutBack = async () => {
		if (this.state.statementsPutBackThisSession.length === 0) return;

		const dbResult = await BookkeepingService.loadEngagementTransactions({ clientId: this.props.clientId });
		if (!dbResult || !dbResult.transactions) {
			alert.warning("The books could not be re-read after a statement was put back, so the rows it restored may not show until the page is reloaded.", { autoClose: 10000 });
			return;
		}

		const booksAsTheServerHasThem = BookkeepingService.mapEngagementTransactionsForTable({ transactions: dbResult.transactions });
		await this.update({ transactionsBeforeImport: booksAsTheServerHasThem, statementsPutBackThisSession: [] });
	};

	pullRegistryTransactionsForGeneration = async ({ sessionTransactions }) => {
		const { fiscalPeriod, sessionStatementFileHashes } = this.state;
		if (!fiscalPeriod) return [];

		const registryPull = await BookkeepingService.collectRegistryTransactionsForPeriod({
			clientId: this.props.clientId,
			periodStart: fiscalPeriod.periodStart,
			periodEnd: fiscalPeriod.periodEnd,
			sessionFileHashes: sessionStatementFileHashes,
			sessionTransactions,
		});

		if (registryPull.error) {
			alert.warning(`Statements already on file could not be checked (${registryPull.error}) — these books are built from this session's imports only.`, { autoClose: 9000 });
			return [];
		}

		if (registryPull.transactions.length > 0) {
			const statementLabels = registryPull.statementsUsed.map(statement => statement.fileName || statement.accountLabel).filter(Boolean).join(", ");
			alert.info(
				`${registryPull.transactions.length} transaction${registryPull.transactions.length === 1 ? "" : "s"} dated inside this fiscal period came from statements already on the client's record (${statementLabels}) — statements imported in an earlier session or year that were not re-uploaded now.`,
				{ autoClose: 12000 }
			);
		}
		if (registryPull.duplicateCount > 0) {
			alert.info(`${registryPull.duplicateCount} transaction${registryPull.duplicateCount === 1 ? "" : "s"} already on file matched this session's imports exactly and ${registryPull.duplicateCount === 1 ? "was" : "were"} not added twice.`, { autoClose: 9000 });
		}

		return registryPull.transactions;
	};

	mergeIntoExistingBooks = ({ incomingTransactions, importedFileNames }) => {
		const existingTransactions = this.state.transactionsBeforeImport;
		if (!existingTransactions) return { transactions: incomingTransactions, importedFileNames };

		const { transactions, addedCount, skippedTransactions } = BookkeepingService.mergeImportedTransactions({
			existingTransactions,
			incomingTransactions,
		});

		const previousFileNames = this.state.importedFileNames || [];
		const mergedFileNames = [...new Set([...previousFileNames, ...(importedFileNames || [])])];

		if (skippedTransactions.length > 0) {
			alert.warning(
				`${addedCount} transaction${addedCount === 1 ? "" : "s"} added. ${skippedTransactions.length} ${skippedTransactions.length === 1 ? "was" : "were"} already in these books (same date, amount and description) and ${skippedTransactions.length === 1 ? "was" : "were"} not added again — re-check if you expected a genuine repeat.`,
				{ autoClose: 14000 }
			);
		} else {
			alert.info(`${addedCount} transaction${addedCount === 1 ? "" : "s"} added to the ${existingTransactions.length} already in these books.`, { autoClose: 9000 });
		}

		return { transactions, importedFileNames: mergedFileNames };
	};

	onGenerateBookkeepingClicked = async () => {
		let { cwPriorYearEnd, bookingPeriod } = this.state;

		await this.update({ loading: true, sessionStartTime: Date.now() });

		try {
			await this.persistClientIndustry();

			const { transactions, invalidCount, importedFileNames } = await this.collectGeneratedTransactions();

			if (invalidCount > 0) {
				alert.error(`${invalidCount} row(s) have missing or unrecognizable date or amount values. Please fix the statement file(s) and re-import.`);
				await this.update({ loading: false });
				return;
			}

			const registrationResults = await this.fileNonStatementSourcesInRegistry({ transactions, importedFileNames });
			await this.rememberStatementsPutBackOnTheShelf({ registrationResults });
			await this.rereadTheBooksIfStatementsWerePutBack();

			const transactionsAlreadyInBooks = this.state.transactionsBeforeImport || [];
			const registryTransactions = await this.pullRegistryTransactionsForGeneration({
				sessionTransactions: [...transactions, ...transactionsAlreadyInBooks],
			});

			const cadTransactions = await BookkeepingService.applyCurrencyConversions({ transactions: [...transactions, ...registryTransactions], currencyByAccount: this.state.currencyByBankAccount });

			const { fiscalPeriod } = this.state;
			let transactionsForTable = cadTransactions;
			let periodExclusions = null;
			if (fiscalPeriod) {
				this.warnWhenCaseWareYearEndDisagrees({ fiscalPeriod, cwPriorYearEnd, bookingPeriod });

				const { kept, excludedBefore, excludedAfter, undated } = BookkeepingService.filterTransactionsToPeriod({
					transactions: cadTransactions,
					periodStart: fiscalPeriod.periodStart,
					periodEnd: fiscalPeriod.periodEnd,
				});
				transactionsForTable = kept;

				if (undated.length > 0) {
					alert.warning(
						`${undated.length} transaction${undated.length === 1 ? " has" : "s have"} no date, so the fiscal-period cutoff cannot place ${undated.length === 1 ? "it" : "them"}. ${undated.length === 1 ? "It is" : "They are"} included in these books — give ${undated.length === 1 ? "it" : "them"} a date to be sure ${undated.length === 1 ? "it belongs" : "they belong"} to this period.`,
						{ autoClose: 12000 }
					);
				}

				const alreadyHeldOut = this.state.transactionsBeforeImport ? this.state.periodExclusions : null;
				const excludedCount = excludedBefore.length + excludedAfter.length;
				if (excludedCount > 0 || alreadyHeldOut) {
					periodExclusions = {
						periodStart: fiscalPeriod.periodStart,
						periodEnd: fiscalPeriod.periodEnd,
						excludedBefore: [...(alreadyHeldOut ? alreadyHeldOut.excludedBefore : []), ...excludedBefore],
						excludedAfter: [...(alreadyHeldOut ? alreadyHeldOut.excludedAfter : []), ...excludedAfter],
					};
				}
				if (excludedCount > 0) {
					alert.info(
						`${excludedCount} transaction${excludedCount > 1 ? "s" : ""} dated outside ${moment(fiscalPeriod.periodStart).format("MMM D, YYYY")} – ${moment(fiscalPeriod.periodEnd).format("MMM D, YYYY")} ${excludedCount > 1 ? "were" : "was"} held out of these books (${excludedBefore.length} before the period, ${excludedAfter.length} after). They are listed in the review report.`,
						{ autoClose: 10000 }
					);
				}
			} else {
				const outsidePeriod = BookkeepingService.findTransactionsOutsidePeriod({ transactions: cadTransactions, priorYearEnd: cwPriorYearEnd, periodMonths: bookingPeriod });
				if (outsidePeriod) {
					alert.warning(
						`${outsidePeriod.outOfRangeCount} transaction${outsidePeriod.outOfRangeCount > 1 ? "s" : ""} fall outside the expected period (${outsidePeriod.periodStartLabel} – ${outsidePeriod.periodEndLabel}). These may be from the wrong year. Proceeding anyway.`,
						{ autoClose: 8000 }
					);
				}
			}

			const columnMap = this.state.glReferenceColumnMap;
			let glReferenceForTable = null;
			if (this.state.glReferenceFile && columnMap.account >= 0 && columnMap.description >= 0) {
				glReferenceForTable = BookkeepingService.buildGeneralLedgerReference({
					rows: this.state.glReferenceRows,
					accountColumnIndex: columnMap.account,
					descriptionColumnIndex: columnMap.description,
					taxColumnIndex: columnMap.tax,
					debitColumnIndex: columnMap.debit,
					creditColumnIndex: columnMap.credit,
					dateColumnIndex: columnMap.date,
					accountNumberColumnIndex: columnMap.accountNumber,
				});
			}

			const { transactions: mergedTransactions, importedFileNames: mergedFileNames } = this.mergeIntoExistingBooks({
				incomingTransactions: transactionsForTable,
				importedFileNames,
			});

			await this.update({
				transactions: mergedTransactions,
				transactionsBeforeImport: null,
				periodExclusions,
				loading: false,
				wizardOpen: false,
				importedFileNames: mergedFileNames,
				glReferenceForTable,
			});
		} catch (error) {
			console.error("Error fetching transactions:", error);
			await this.update({ loading: false });
		}
	};

	componentDidMount = async () => {
		try {
			await this.readWhatTheToolOpensWith();
			await this.readWhetherTheStatementsCanBeBuiltOn();
		} finally {
			await this.update({ stillReadingTheBooks: false });
		}
	};

	readWhetherTheStatementsCanBeBuiltOn = async () => {
		let { wpfEngagementId } = this.state;
		if (!wpfEngagementId) return;

		let answer = await BookkeepingService.fetchStatementsThatDoNotReconcile({ wpfEngagementId });
		await this.update({
			statementsWereChecked: answer.answered,
			statementsToPutRight: answer.statements,
			statementDifferenceWasAccepted: !!(answer.acceptance && answer.acceptance.accepted),
		});
	};

	onAcceptStatementsOpen = () => this.update({ acceptStatementsOpen: true, acceptStatementsReason: "" });

	onAcceptStatementsClose = () => this.update({ acceptStatementsOpen: false });

	onAcceptStatementsReasonChange = event => this.update({ acceptStatementsReason: event.target.value });

	onAcceptStatementsConfirm = async () => {
		let { wpfEngagementId, acceptStatementsReason } = this.state;

		await this.update({ acceptStatementsSaving: true });
		let result = await BookkeepingService.acceptStatementsThatDoNotReconcile({ wpfEngagementId, reason: acceptStatementsReason });
		await this.update({ acceptStatementsSaving: false });

		if (!result.recorded) {
			alert.error(result.reason || "That could not be recorded just now.");
			return;
		}

		await this.update({ acceptStatementsOpen: false });
		await this.readWhetherTheStatementsCanBeBuiltOn();
	};

	readWhatTheToolOpensWith = async () => {
		let { clientId } = this.props;

		try {
			let clientResp = await ClientService.getClient({ clientId });
			let builtInIndustry = clientResp && clientResp.client ? clientResp.client.industry : null;

			let customFields = await ClientService.fetchCustomFields({ clientId });
			let clientCustomFields = Array.isArray(customFields) ? customFields : [];
			let industryCustomField = this.findIndustryCustomField(clientCustomFields);
			let customFieldIndustry = industryCustomField ? industryCustomField.value : null;

			let resolvedIndustry = builtInIndustry || customFieldIndustry || null;
			await this.update({ clientIndustry: resolvedIndustry, clientCustomFields });
		} catch (_) {}

		try {
			let dbResult = await BookkeepingService.loadEngagementTransactions({ clientId });
			if (dbResult && dbResult.transactions && dbResult.transactions.length > 0) {
				let transactions = BookkeepingService.mapEngagementTransactionsForTable({ transactions: dbResult.transactions });

				let detail = dbResult.engagementDetail;
				let loadedIndustry = "";
				if (detail) {
					if (detail.csrs_checklist && detail.csrs_checklist.operatingProfile && detail.csrs_checklist.operatingProfile.industry) {
						loadedIndustry = detail.csrs_checklist.operatingProfile.industry;
					} else if (detail.engagement_context && detail.engagement_context.operating_profile && detail.engagement_context.operating_profile.industry) {
						loadedIndustry = detail.engagement_context.operating_profile.industry;
					}
				}
				const loadedGlEntries = Array.isArray(dbResult.glReferenceEntries) ? dbResult.glReferenceEntries : [];
				const loadedBankSideAccounts = Array.isArray(dbResult.bankSideAccounts) ? dbResult.bankSideAccounts : [];
				const loadedFiscalPeriod = detail && detail.period_end && detail.period_confirmed_at
					? { fiscalYearEnd: detail.fiscal_year_end, periodStart: detail.period_start, periodEnd: detail.period_end }
					: null;

				await this.update({
					transactions,
					province: BookkeepingService.getKnownProvinceOrEmpty({ province: detail ? detail.province : "" }),
					industry: loadedIndustry,
					hasTax: true,
					gstHstAccount: dbResult.gstHstAccount || null,
					wizardOpen: false,
					wpfEngagementId: detail ? detail.id : null,
					csrsChecklist: detail ? detail.csrs_checklist || null : null,
					engagementState: detail ? detail.state || null : null,
					loadedGlEntries,
					loadedBankSideAccounts,
					fiscalPeriod: loadedFiscalPeriod,
					periodConfirmed: !!loadedFiscalPeriod,
				});
			} else if (dbResult && dbResult.ok) {
				await this.update({ wizardOpen: true });
			} else {
				await this.update({ booksCouldNotBeRead: true });
			}
		} catch (_) {
			await this.update({ booksCouldNotBeRead: true });
		}

		let result = await PlaidService.fetchAccounts({ searchTerm: "", clientId });
		let bankAccounts = result.map(account => ({
			id: account.id,
			title: account.name,
			name: account.name,
			isChecked: false
		}));
		await this.update({ connectedAccounts: bankAccounts });

		if (this.state.wizardOpen) this.ensureOpeningContext();
	};

	onStartDateChange = async startDate => { await this.update({ startDate, plaidDatesTouched: true }); };
	onEndDateChange = async endDate => { await this.update({ endDate, plaidDatesTouched: true }); };

	checkBoxSelected = index => {
		this.setState(prev => {
			const connectedAccounts = prev.connectedAccounts.map((account, accountIndex) =>
				accountIndex === index ? { ...account, isChecked: !account.isChecked } : account
			);
			return { connectedAccounts };
		});
	};

	onTransactionsUpdate = async updatedTransactions => {
		await this.update({ transactions: updatedTransactions });
	};

	findIndustryCustomField = customFields => {
		const industryCustomField = (customFields || []).find(customField => (customField.name || "").trim().toLowerCase() === "industry");
		return industryCustomField || null;
	};

	persistClientIndustry = async () => {
		const chosenIndustry = toCanonicalIndustry(this.state.industry) || toCanonicalIndustry(this.state.clientIndustry);
		if (!chosenIndustry) return;

		const industryCustomField = this.findIndustryCustomField(this.state.clientCustomFields);
		const builtInIsUpToDate = chosenIndustry === this.state.clientIndustry;
		const customFieldIsUpToDate = !industryCustomField || industryCustomField.value === chosenIndustry;
		if (builtInIsUpToDate && customFieldIsUpToDate) return;

		const updateParams = { clientId: this.props.clientId, industry: chosenIndustry };
		let updatedCustomFields = this.state.clientCustomFields;
		if (industryCustomField && !customFieldIsUpToDate) {
			updatedCustomFields = this.state.clientCustomFields.map(customField => (customField.id === industryCustomField.id ? { ...customField, value: chosenIndustry } : customField));
			updateParams.customFields = updatedCustomFields;
		}

		const response = await ClientService.updateClient(updateParams);
		if (response) await this.update({ clientIndustry: chosenIndustry, industry: chosenIndustry, clientCustomFields: updatedCustomFields });
	};

	onClearTransactions = () => this.update({ showClearConfirm: true });

	onClearConfirmClose = async ({ confirmed }) => {
		await this.update({ showClearConfirm: false });
		if (!confirmed) return;

		let { clientId } = this.props;
		let { wpfEngagementId } = this.state;

		if (!wpfEngagementId) {
			alert.error("This client's books have not loaded, so there is no year to clear. Reload the page rather than clearing — clearing without a year could empty a different one.");
			return;
		}

		await this.update({ loading: true });
		try {
			let result = await BookkeepingService.clearEngagementTransactions({ clientId, wpfEngagementId });
			if (result && result.success !== false) {
				let cleared = result.cleared || {};
				alert.success(
					`Cleared ${cleared.transactions || 0} transactions, ${cleared.adjusting_entries || 0} AJEs, ` +
					`${cleared.agent_decisions || 0} agent decisions. ` +
					`${cleared.bank_statements_kept || 0} statement${(cleared.bank_statements_kept || 0) === 1 ? "" : "s"} kept — generate again to rebuild from them.`
				);
				await this.update({
					...emptyWizardImportState(),
					wizardOpen: true,
					loading: false,
					engagementState: "IN_PROGRESS",
				});
			} else {
				alert.error(result && result.error ? result.error : "Failed to clear transactions.");
				await this.update({ loading: false });
			}
		} catch (error) {
			console.error("Error clearing transactions:", error);
			alert.error("Failed to clear transactions: " + (error.message || "unknown error"));
			await this.update({ loading: false });
		}
	};

	thereAreStatementsToPutRight = () => {
		const { statementsWereChecked, statementsToPutRight, engagementState } = this.state;
		if (!statementsWereChecked) return false;
		if (engagementState === "FINALIZED") return false;
		return statementsToPutRight.length > 0;
	};

	theBooksCannotBeBuiltOnYet = () => this.thereAreStatementsToPutRight() && !this.state.statementDifferenceWasAccepted;

	readTotalUnaccountedFor = () => {
		const amounts = this.state.statementsToPutRight.map(statement => this.readHowMuchIsUnaccountedFor({ statement }) || 0);
		const total = amounts.reduce((running, amount) => running + amount, 0);
		return Math.round(total * 100) / 100;
	};

	describeWhatIsWrongWith = ({ statement }) => {
		if (statement.kind === "reading_does_not_reach_the_closing_balance") {
			return "What was read off it does not reach the closing balance it prints";
		}
		if (statement.kind === "not_tied_to_an_account") {
			return "Not tied to any bank account in these books";
		}
		const lineWord = statement.lineCount === 1 ? "line" : "lines";
		return `The books are short of its ${statement.lineCount} ${lineWord}`;
	};

	readHowMuchIsUnaccountedFor = ({ statement }) => {
		const unaccountedFor = statement.kind === "reading_does_not_reach_the_closing_balance" ? statement.readingIsOutBy : statement.shortBy;
		if (unaccountedFor === null || unaccountedFor === undefined) return null;
		return Math.abs(Number(unaccountedFor));
	};

	renderStatementToPutRight = statement => {
		const unaccountedFor = this.readHowMuchIsUnaccountedFor({ statement });
		const amountShown = unaccountedFor === null ? "—" : UtilityService.formatSignedCurrency({ value: unaccountedFor });

		return (
			<li className="bk-block__statement" key={statement.statementId}>
				<span className="bk-block__statement-account">{statement.accountLabel || "Account not read"}</span>
				<span className="bk-block__statement-amount">{amountShown}</span>
				<span className="bk-block__statement-fault">{this.describeWhatIsWrongWith({ statement })}</span>
				<span className="bk-block__statement-period">{statement.periodStart} to {statement.periodEnd}</span>
			</li>
		);
	};

	renderStatementsMustBePutRight = () => {
		const { statementsToPutRight } = this.state;
		const howMany = statementsToPutRight.length;

		return (
			<div className="container">
				<div className="bk-block">
					<div className="bk-block__head">
						<span className="bk-block__chip"><Icon.AlertTriangle size={18} /></span>
						<div className="bk-block__heading">
							<h3 className="bk-block__title">
								{howMany} statement{howMany === 1 ? "" : "s"} {howMany === 1 ? "does" : "do"} not reconcile
							</h3>
							<p className="bk-block__subtitle">
								The books on this file are built from these statements, so they cannot be worked on until each one
								adds up. Put them right under Statements and this will clear.
							</p>
						</div>
					</div>

					<ul className="bk-block__statements">{statementsToPutRight.map(this.renderStatementToPutRight)}</ul>

					<div className="bk-block__actions">
						<button type="button" className="tk-button tk-button--primary" onClick={this.onImportedFilesOpen}>
							<Icon.FileText size={15} /> Review statements
						</button>
						<button type="button" className="tk-text-action" onClick={this.onAcceptStatementsOpen}>
							Work on these books anyway
						</button>
					</div>
				</div>
			</div>
		);
	};

	renderStatementDifferenceWasAccepted = () => {
		const howMany = this.state.statementsToPutRight.length;
		const totalUnaccountedFor = UtilityService.formatSignedCurrency({ value: this.readTotalUnaccountedFor() });

		return (
			<div className="bk-accepted">
				<Icon.AlertTriangle size={15} className="bk-accepted__icon" />
				<span className="bk-accepted__text">
					These books are being worked on while {howMany} statement{howMany === 1 ? "" : "s"} {howMany === 1 ? "does" : "do"} not
					reconcile. <strong>{totalUnaccountedFor}</strong> is unaccounted for, so the bank balance and anything built from it
					are wrong by up to that amount.
				</span>
				<button type="button" className="tk-text-action" onClick={this.onImportedFilesOpen}>
					Put them right
				</button>
			</div>
		);
	};

	renderAcceptStatementsModal = () => {
		const { acceptStatementsOpen, acceptStatementsReason, acceptStatementsSaving, statementsToPutRight } = this.state;
		const howMany = statementsToPutRight.length;
		const totalUnaccountedFor = UtilityService.formatSignedCurrency({ value: this.readTotalUnaccountedFor() });
		const canRecord = acceptStatementsReason.trim().length > 0 && !acceptStatementsSaving;

		return (
			<ToolsModal
				open={acceptStatementsOpen}
				onClose={this.onAcceptStatementsClose}
				title="Work on these books anyway?"
				width={560}
				footer={
					<>
						<span />
						<span className="tk-modal-actions">
							<button type="button" className="tk-button tk-button--secondary" onClick={this.onAcceptStatementsClose}>Cancel</button>
							<button type="button" className="tk-button tk-button--primary" disabled={!canRecord} onClick={this.onAcceptStatementsConfirm}>
								{acceptStatementsSaving ? "Recording…" : "Accept and continue"}
							</button>
						</span>
					</>
				}
			>
				<div>
					<p className="bk-accept__consequence">
						{howMany} statement{howMany === 1 ? "" : "s"} on this file {howMany === 1 ? "does" : "do"} not reconcile, leaving{" "}
						<strong>{totalUnaccountedFor}</strong> unaccounted for.
					</p>
					<p className="bk-accept__consequence">
						The books here are built from those statements. Working on them now means the bank balance, the trial balance,
						and any financial statements produced from this file will be wrong by up to that amount, and nothing later in
						the engagement will catch it.
					</p>
					<div className="tk-form-field">
						<label className="tk-form-label" htmlFor="bk-accept-reason">
							Why these books can be worked on <span className="tk-form-required">*</span>
						</label>
						<input
							id="bk-accept-reason"
							type="text"
							className="tk-input"
							value={acceptStatementsReason}
							onChange={this.onAcceptStatementsReasonChange}
							placeholder="e.g. client is sending the missing May statement, continuing on the rest"
						/>
						<span className="tk-form-help">Recorded against this engagement with your name and today's date.</span>
					</div>
				</div>
			</ToolsModal>
		);
	};

	isAddingToExistingBooks = () => !!this.state.transactionsBeforeImport;

	onWizardNext = () => this.update({ wizardStep: this.state.wizardStep + 1 });
	onWizardBack = () => this.update({ wizardStep: this.state.wizardStep - 1 });
	onWizardClose = () => {
		if (this.state.transactions.length === 0 && this.props.onExit) {
			this.props.onExit();
			return;
		}
		this.update({ wizardOpen: false, transactionsBeforeImport: null });
	};
	onImportedFilesOpen = () => this.update({ importedFilesModalOpen: true });

	onImportedFilesClose = async () => {
		await this.update({ importedFilesModalOpen: false });
		await this.readWhetherTheStatementsCanBeBuiltOn();
	};

	onImportedFilesChanged = async () => {
		const dbResult = await BookkeepingService.loadEngagementTransactions({ clientId: this.props.clientId });
		if (!dbResult || !dbResult.transactions) return;

		const transactions = BookkeepingService.mapEngagementTransactionsForTable({ transactions: dbResult.transactions });
		await this.update({ transactions });
		await this.readWhetherTheStatementsCanBeBuiltOn();
	};

	onAddFilesOfKind = ({ kind }) => {
		const stepForKind = {
			bank_statement: { importMode: "manual", wizardStep: 2 },
			gl_reference: { importMode: "manual", wizardStep: 3 },
			caseware_reference: { importMode: "caseware", wizardStep: 1 },
		};
		this.onWizardOpen(stepForKind[kind]);
		if (kind === "bank_statement") this.loadBankAccountsForStatementsScreen();
	};

	onWizardOpen = (startingPoint = null) => {
		const transactionsAlreadyInBooks = this.state.transactions || [];
		const isAddingToExistingBooks = transactionsAlreadyInBooks.length > 0;

		this.update({
			...emptyWizardImportState(),
			wizardOpen: true,
			transactions: transactionsAlreadyInBooks,
			transactionsBeforeImport: isAddingToExistingBooks ? transactionsAlreadyInBooks : null,
			importedFileNames: isAddingToExistingBooks ? this.state.importedFileNames : null,
			periodExclusions: isAddingToExistingBooks ? this.state.periodExclusions : null,
			openingBalancesDone: isAddingToExistingBooks,
			importMode: isAddingToExistingBooks ? "manual" : "",
			cwSkipped: isAddingToExistingBooks,
			wizardStep: isAddingToExistingBooks ? 2 : 1,
			...(isAddingToExistingBooks && startingPoint ? startingPoint : {}),
		});
		if (!isAddingToExistingBooks) this.ensureOpeningContext();
	};

	ensureOpeningContext = async () => {
		try {
			let res = await BookkeepingService.ensureEngagement({ clientId: this.props.clientId });
			if (res && res.wpfEngagementId) {
				const fiscalPeriod = res.periodConfirmed
					? { fiscalYearEnd: res.fiscalYearEnd, periodStart: res.periodStart, periodEnd: res.periodEnd }
					: null;
				await this.update({
					wpfEngagementId: res.wpfEngagementId,
					isBootstrapFile: !!res.isBootstrap,
					engagementContextError: "",
					fiscalPeriod,
					periodConfirmed: !!res.periodConfirmed,
					periodDraftYearEnd: this.state.periodDraftYearEnd || res.fiscalYearEnd || "",
					province: this.state.province || BookkeepingService.getKnownProvinceOrEmpty({ province: res.province }),
				});
				this.applyPeriodToPlaidDates();
				return;
			}
			await this.update({ engagementContextError: (res && res.error) || "the server returned no engagement" });
		} catch (error) {
			await this.update({ engagementContextError: error.message || String(error) });
		}
	};

	applyPeriodToPlaidDates = () => {
		const { fiscalPeriod, plaidDatesTouched } = this.state;
		if (!fiscalPeriod) return;
		if (plaidDatesTouched) return;
		this.update({
			startDate: new Date(`${fiscalPeriod.periodStart}T00:00:00`),
			endDate: new Date(`${fiscalPeriod.periodEnd}T00:00:00`),
		});
	};

	onConfirmFiscalPeriod = async () => {
		const { periodDraftYearEnd } = this.state;

		const derived = this.derivePeriodDraft();
		if (!derived || derived.error) {
			await this.update({ periodDraftError: (derived && derived.error) || "Set the period end first." });
			return;
		}

		const res = await BookkeepingService.ensureEngagement({
			clientId: this.props.clientId,
			fiscalYearEnd: periodDraftYearEnd,
			periodStart: derived.periodStart,
			periodEnd: derived.periodEnd,
			periodType: derived.periodType,
			periodReason: derived.periodReason,
		});
		if (!res || !res.wpfEngagementId) {
			await this.update({ periodDraftError: (res && res.error) || "The fiscal period could not be saved." });
			return;
		}

		await this.update({
			wpfEngagementId: res.wpfEngagementId,
			isBootstrapFile: !!res.isBootstrap,
			fiscalPeriod: {
				fiscalYearEnd: periodDraftYearEnd,
				periodStart: derived.periodStart,
				periodEnd: derived.periodEnd,
				periodType: derived.periodType,
				periodReason: derived.periodReason,
				isFullYear: derived.isFullYear,
			},
			periodConfirmed: true,
			periodDraftError: "",
			engagementContextError: "",
		});
		this.applyPeriodToPlaidDates();
		await this.rescopeImportedTransactionsToPeriod();
	};

	warnWhenCaseWareYearEndDisagrees = ({ fiscalPeriod, cwPriorYearEnd, bookingPeriod }) => {
		if (!cwPriorYearEnd) return;
		const caseWareYearEnd = UtilityService.addMonthsToIsoDate({ isoDate: cwPriorYearEnd, monthsToAdd: bookingPeriod });
		if (!caseWareYearEnd || caseWareYearEnd === fiscalPeriod.periodEnd) return;
		alert.warning(
			`The CaseWare data suggests a year-end of ${moment(caseWareYearEnd).format("MMM D, YYYY")}, but this file's fiscal period ends ${moment(fiscalPeriod.periodEnd).format("MMM D, YYYY")}. Confirm the fiscal period is right before relying on these books.`,
			{ autoClose: 12000 }
		);
	};

	onChangeFiscalPeriod = () => {
		this.update({ periodConfirmed: false });
	};

	rescopeImportedTransactionsToPeriod = async () => {
		const { fiscalPeriod, transactions, periodExclusions } = this.state;
		if (!fiscalPeriod) return;

		const heldOutBefore = periodExclusions ? periodExclusions.excludedBefore : [];
		const heldOutAfter = periodExclusions ? periodExclusions.excludedAfter : [];
		const transactionsInHand = [...(transactions || []), ...heldOutBefore, ...heldOutAfter];

		const archivedRows = transactionsInHand.filter(transaction => transaction.isArchived);
		const archivedRowKeys = new Set(archivedRows.map(transaction => BookkeepingService.buildDuplicateKeyForTransaction({ transaction })).filter(Boolean));

		const registryPull = await BookkeepingService.collectRegistryTransactionsForPeriod({
			clientId: this.props.clientId,
			periodStart: fiscalPeriod.periodStart,
			periodEnd: fiscalPeriod.periodEnd,
			sessionFileHashes: this.state.sessionStatementFileHashes,
			sessionTransactions: transactionsInHand,
		});
		const pulledFromRecord = registryPull.error ? [] : registryPull.transactions;
		const transactionsFromRecord = pulledFromRecord.filter(
			transaction => !archivedRowKeys.has(BookkeepingService.buildDuplicateKeyForTransaction({ transaction }))
		);

		const liveInHand = transactionsInHand.filter(transaction => !transaction.isArchived);
		const everythingAvailable = [...liveInHand, ...transactionsFromRecord];
		if (everythingAvailable.length === 0 && archivedRows.length === 0) return;

		const { kept, excludedBefore, excludedAfter } = BookkeepingService.filterTransactionsToPeriod({
			transactions: everythingAvailable,
			periodStart: fiscalPeriod.periodStart,
			periodEnd: fiscalPeriod.periodEnd,
		});

		const liveBefore = (transactions || []).filter(transaction => !transaction.isArchived);
		const broughtInCount = kept.length - liveBefore.length;
		const excludedCount = excludedBefore.length + excludedAfter.length;

		await this.update({
			transactions: [...kept, ...archivedRows],
			periodExclusions: excludedCount > 0
				? { periodStart: fiscalPeriod.periodStart, periodEnd: fiscalPeriod.periodEnd, excludedBefore, excludedAfter }
				: null,
		});

		const periodLabel = `${moment(fiscalPeriod.periodStart).format("MMM D, YYYY")} – ${moment(fiscalPeriod.periodEnd).format("MMM D, YYYY")}`;
		if (registryPull.error) {
			alert.warning(`Re-scoped to ${periodLabel}, but transactions already on this client's record could not be checked (${registryPull.error}), so any that belong to the new period were not brought back.`, { autoClose: 12000 });
		}
		if (broughtInCount > 0) {
			alert.info(`Re-scoped to ${periodLabel}: ${broughtInCount} transaction${broughtInCount === 1 ? "" : "s"} now fall inside the period and ${broughtInCount === 1 ? "was" : "were"} brought into the books (${transactionsFromRecord.length} from statements already on record). ${excludedCount} held out.`, { autoClose: 12000 });
			return;
		}
		if (broughtInCount < 0) {
			alert.info(`Re-scoped to ${periodLabel}: ${Math.abs(broughtInCount)} transaction${broughtInCount === -1 ? "" : "s"} now fall outside the period and ${broughtInCount === -1 ? "was" : "were"} held out of the books. Nothing was deleted — they stay on the client's record and in the review report.`, { autoClose: 12000 });
		}
	};

	onFiscalPeriodChangedFromSettings = async ({ derivedPeriod }) => {
		const res = await BookkeepingService.ensureEngagement({
			clientId: this.props.clientId,
			fiscalYearEnd: derivedPeriod.periodEnd,
			periodStart: derivedPeriod.periodStart,
			periodEnd: derivedPeriod.periodEnd,
			periodType: derivedPeriod.periodType,
			periodReason: derivedPeriod.periodReason,
		});
		if (!res || res.error || !res.wpfEngagementId) {
			return { error: (res && res.error) || "The fiscal period could not be saved." };
		}

		await this.update({
			wpfEngagementId: res.wpfEngagementId,
			fiscalPeriod: {
				fiscalYearEnd: derivedPeriod.periodEnd,
				periodStart: derivedPeriod.periodStart,
				periodEnd: derivedPeriod.periodEnd,
				periodType: derivedPeriod.periodType,
				periodReason: derivedPeriod.periodReason,
				isFullYear: derivedPeriod.isFullYear,
			},
			periodConfirmed: true,
			periodDraftYearEnd: derivedPeriod.periodEnd,
			periodDraftFirstDay: derivedPeriod.periodStart,
		});

		await this.rescopeImportedTransactionsToPeriod();
		return { saved: true };
	};

	onChooseImportMode = mode => {
		if (mode === "manual") {
			this.update({ importMode: "manual", cwSkipped: true, wizardStep: 2 });
			this.loadBankAccountsForStatementsScreen();
			return;
		}
		this.update({ importMode: mode, cwSkipped: false, glSource: "" });
	};

	onBackToModeChooser = () => {
		this.update({ importMode: "", cwSkipped: false, glSource: "", wizardStep: 1 });
	};

	onChooseGlSource = source => {
		if (!source.available) {
			alert.info(`${source.title} import is coming soon.`);
			return;
		}
		this.update({ glSource: source.key });
		this.openGlSourceFilePicker(source.key);
	};

	openGlSourceFilePicker = sourceKey => {
		const isExcelSource = EXCEL_GL_SOURCES.includes(sourceKey);
		const fileInput = document.createElement("input");
		fileInput.type = "file";
		fileInput.accept = isExcelSource ? ".xlsx" : ".csv";
		fileInput.onchange = event => {
			const file = event.target.files && event.target.files[0];
			if (!file) return;
			if (isExcelSource) this.onGlExcelFileSelected(sourceKey, file);
			else this.onGlReferenceFileSelected(file);
		};
		fileInput.click();
	};

	onGlSourceBack = () => {
		this.update({ glSource: "", glOpeningBalances: [], glOpeningReviewOpen: false, glOpeningReviewDone: false });
	};

	onGlExcelFileSelected = async (source, file) => {
		if (!file) return;
		await this.update({ glBankDetecting: true });
		const parsed = await BookkeepingService.parseGlExcel({ clientId: this.props.clientId, file, source, wpfEngagementId: this.state.wpfEngagementId });
		await this.update({ glBankDetecting: false });
		if (!parsed || !Array.isArray(parsed.rows)) {
			alert.error(parsed && parsed.error ? parsed.error : "Couldn't read that export — check the file and try again.");
			return;
		}
		await this.update({
			glReferenceFile: file,
			glReferenceHeaders: parsed.headers || [],
			glReferenceRows: parsed.rows,
			glReferenceColumnMap: parsed.columnMap || this.state.glReferenceColumnMap,
			glOpeningBalances: parsed.openingBalances || [],
			glOpeningReviewDone: false,
			glLayout: parsed.layout || "",
			glEntityName: parsed.entityName || "",
		});
		if (!this.state.industry && parsed.entityName) this.inferIndustryAsync(parsed.entityName);
		this.onGlStepNext();
	};

	onGlStepNext = async () => {
		const alreadyReviewed = this.state.glOpeningReviewDone;
		const needsOpeningReview = this.state.glOpeningBalances.length > 0 && !alreadyReviewed;
		if (needsOpeningReview) {
			if (!this.state.wpfEngagementId) await this.ensureOpeningContext();
			await this.update({ glOpeningReviewOpen: true });
			return;
		}
		await this.onGlReferenceNextClicked();
	};

	onGlOpeningReviewDone = async () => {
		await this.update({ glOpeningReviewOpen: false, glOpeningReviewDone: true });
		await this.onGlReferenceNextClicked();
	};

	onGlReferenceNextClicked = async () => {
		const { clientId } = this.props;
		if (this.glBankDetectionRunning) return;

		const columnMap = this.state.glReferenceColumnMap;
		const hasRequiredColumns = this.state.glReferenceFile && columnMap.account >= 0 && columnMap.description >= 0;
		if (!hasRequiredColumns) {
			alert.error("Tell us which columns hold the account and the description before continuing.");
			return;
		}

		this.glBankDetectionRunning = true;
		await this.update({ glBankDetecting: true });

		try {
			const built = BookkeepingService.buildGeneralLedgerReference({
				rows: this.state.glReferenceRows,
				accountColumnIndex: columnMap.account,
				descriptionColumnIndex: columnMap.description,
				taxColumnIndex: columnMap.tax,
				debitColumnIndex: columnMap.debit,
				creditColumnIndex: columnMap.credit,
				dateColumnIndex: columnMap.date,
				accountNumberColumnIndex: columnMap.accountNumber,
			});
			const accountList = built.accountList || [];

			const detection = await BookkeepingService.identifyLikelyBankAccounts({ clientId, accountNames: accountList });
			if (detection.error) {
				alert.warning(`We couldn't work out which accounts are bank accounts (${detection.error}). Pick them yourself on the next screen.`, { autoClose: 10000 });
			}

			await this.update({
				glAccountList: accountList,
				glDetectedBankAccounts: detection.bankAccountNames || [],
				wizardStep: 2,
			});
		} catch (error) {
			alert.error(`Couldn't read the general ledger: ${error.message || error}`);
		} finally {
			this.glBankDetectionRunning = false;
			await this.update({ glBankDetecting: false });
		}
	};

	onBankReviewDraftChange = draft => {
		this.setState({ bankReviewDraft: draft });
		const uploads = Object.values(draft.fileByAccount || {});
		uploads.forEach(upload => {
			if (!upload || upload.kind !== "spreadsheet") return;
			const fileKey = UtilityService.getFileKey(upload.file);
			const alreadyPrepared = !!this.state.statementColumnsByFile[fileKey];
			if (alreadyPrepared) return;
			this.prepareStatementFile(upload.file);
		});
	};

	loadBankAccountsForStatementsScreen = async () => {
		const { clientId } = this.props;
		const { wpfEngagementId } = this.state;

		await this.update({ bankAccountsLoading: true });
		const [links, shelf, chart] = await Promise.all([
			BookkeepingService.fetchBankAccountLinks({ clientId, wpfEngagementId }),
			BookkeepingService.listImportedFiles({ clientId, wpfEngagementId }),
			BookkeepingService.getClientCoA({ clientId }),
		]);

		if (links.error) alert.error(`The bank accounts on this file could not be read (${links.error}).`);
		if (!shelf || shelf.error) alert.error(`The statements on this client's record could not be read (${(shelf && shelf.error) || "no response"}).`);

		const statementsOnTheShelf = shelf && shelf.bankStatements ? shelf.bankStatements : [];
		const shelfAccountLabels = BookkeepingService.listAccountLabelsOnStatements({ statements: statementsOnTheShelf });
		const bankAccountsOnTheFile = BookkeepingService.listBankAccountsKnownToTheFile({ bankAccountLinks: links.bankAccounts || [], shelfAccountLabels });
		const statementsOnTheShelfByAccount = BookkeepingService.summarizeStatementsOnTheShelfByAccount({ statements: statementsOnTheShelf, accounts: bankAccountsOnTheFile });
		const chartAccountsForBankPicker = chart && chart.accounts ? chart.accounts : [];

		await this.update({
			bankAccountsOnTheFile,
			statementsOnTheShelfByAccount,
			chartAccountsForBankPicker,
			statementAccountsOnRecord: shelfAccountLabels,
			bankAccountsLoading: false,
		});
	};

	onChangeBankAccountCurrency = async ({ bankAccountId, accountName, currency }) => {
		const { clientId } = this.props;
		const { transactions, transactionsBeforeImport, wpfEngagementId, province } = this.state;

		const recorded = await BookkeepingService.setBankAccountCurrency({ bankAccountId, currency, clientId });
		if (!recorded || !recorded.recorded) {
			alert.error((recorded && recorded.reason) || "That account's currency could not be changed.");
			return { changed: false };
		}

		const changeByIdentity = await BookkeepingService.setAccountCurrency({ transactions, accountName, currency });
		const reconverted = BookkeepingService.applyRowChanges({ transactions, changeByIdentity });
		const reconvertedTransactions = reconverted.transactions;
		const rowsThatChanged = reconverted.changedRows;

		if (rowsThatChanged.length > 0) {
			const saved = await BookkeepingService.saveEngagementTransactions({
				engagementId: null,
				clientId,
				wpfEngagementId,
				transactions: BookkeepingService.mapTableTransactionsForSave({ transactions: rowsThatChanged }),
				removeTransactionIds: [],
				province,
			});
			if (!saved || saved.error) {
				alert.error("The account's currency was recorded, but its transactions could not be reconverted. Reload and try again.");
				return { changed: false };
			}
		}

		const booksBeforeImport = transactionsBeforeImport ? BookkeepingService.applyRowChanges({ transactions: transactionsBeforeImport, changeByIdentity }).transactions : transactionsBeforeImport;
		await this.update({ transactions: reconvertedTransactions, transactionsBeforeImport: booksBeforeImport });
		await this.loadBankAccountsForStatementsScreen();

		const reconvertedCount = UtilityService.describeCount({ count: rowsThatChanged.length, singular: "transaction" });
		alert.success(`${accountName} is now held in ${currency}. ${reconvertedCount} reconverted from the amounts the bank printed.`, { autoClose: 8000 });
		return { changed: true };
	};

	onTieBankAccountToChart = async ({ bankAccountId, accountId }) => {
		const outcome = await BookkeepingService.tieBankAccountToChart({ bankAccountId, accountId, clientId: this.props.clientId });
		if (outcome.tied) alert.success(outcome.message);
		else alert.error(outcome.message);
		await this.loadBankAccountsForStatementsScreen();
	};

	renderBankReviewContinueButton = () => {
		const draft = this.state.bankReviewDraft;
		const includedCount = draft ? draft.includedCount : 0;
		const uploads = draft ? Object.values(draft.fileByAccount || {}) : [];
		const hasAnyFile = uploads.length > 0;
		const spreadsheetUploads = uploads.filter(upload => upload.kind === "spreadsheet");
		const allSignsConfirmed = spreadsheetUploads.every(upload => {
			const sign = this.state.statementSignByFile[UtilityService.getFileKey(upload.file)];
			return !!(sign && sign.confirmed);
		});
		const isDisabled = includedCount === 0 || !hasAnyFile || !allSignsConfirmed;

		let titleText;
		if (includedCount === 0) titleText = "Confirm at least one account";
		else if (!hasAnyFile) titleText = "Upload a statement for at least one account";
		else if (!allSignsConfirmed) titleText = "Confirm the sign convention for each statement first";

		const countStatementsAttached = upload => (Array.isArray(upload.files) ? upload.files.length : 1);
		const statementCount = uploads.reduce((count, upload) => count + countStatementsAttached(upload), 0);
		const statements = UtilityService.describeCount({ count: statementCount, singular: "statement" });
		const accounts = UtilityService.describeCount({ count: includedCount, singular: "account" });
		const buttonText = this.isAddingToExistingBooks() ? `Import ${statements}` : `Continue with ${accounts}`;

		return (
			<Button
				text={buttonText}
				onClick={() => this.onBankReviewComplete({ draft })}
				disabled={isDisabled}
				title={titleText}
			/>
		);
	};

	onBankReviewComplete = ({ draft }) => {
		const uploads = Object.entries(draft.fileByAccount || {});
		const pdfGroups = uploads
			.filter(([, upload]) => upload && upload.kind === "pdf" && Array.isArray(upload.files) && upload.files.length > 0)
			.map(([accountName, upload]) => ({ accountName, files: upload.files }));

		const unverifiedGroups = pdfGroups.filter(group => !group.files.every(file => this.state.verifiedStatementFileNames.includes(file.name)));
		if (unverifiedGroups.length > 0) {
			this.update({ statementVerificationGroups: unverifiedGroups, pendingStatementDraft: draft });
			return;
		}

		this.proceedWithStatementDraft({ draft });
	};

	proceedWithStatementDraft = async ({ draft }) => {
		await this.update({
			confirmedBankAccounts: draft.bankAccountNames,
			fileByBankAccount: draft.fileByAccount || {},
			currencyByBankAccount: draft.currencyByAccount || {},
		});

		if (!this.isAddingToExistingBooks()) {
			await this.update({ wizardStep: 3 });
			return;
		}

		const knownProvince = BookkeepingService.getKnownProvinceOrEmpty({ province: this.state.province });
		if (knownProvince) {
			await this.onGenerateBookkeepingClicked();
			return;
		}
		await this.update({ wizardStep: 4 });
	};

	onPickUpUnreadStatements = ({ statementFiles, accountLabels = [] }) => {
		if (!statementFiles || statementFiles.length === 0) return;

		this.update({
			importedFilesModalOpen: false,
			statementVerificationGroups: [{ accountName: null, files: statementFiles }],
			statementAccountsOnRecord: accountLabels,
		});
	};

	listAccountsAStatementCouldBelongTo = () => {
		const { confirmedBankAccounts, glDetectedBankAccounts, statementAccountsOnRecord, statementVerificationGroups, bankAccountsOnTheFile } = this.state;
		const fromTheGroupsBeingVerified = (statementVerificationGroups || []).map(group => group.accountName);
		const fromTheFile = (bankAccountsOnTheFile || []).map(account => account.name);

		const everyKnownAccount = [
			...fromTheFile,
			...(statementAccountsOnRecord || []),
			...(confirmedBankAccounts || []),
			...(glDetectedBankAccounts || []),
			...fromTheGroupsBeingVerified,
		];

		return [...new Set(everyKnownAccount.filter(Boolean))];
	};

	onStatementVerificationComplete = async ({ transactions, fileNames, parsesWithAccounts }) => {
		const groups = this.state.statementVerificationGroups || [];
		const verifiedFileNames = fileNames || groups.flatMap(group => group.files.map(file => file.name));
		const pendingDraft = this.state.pendingStatementDraft;

		const sessionHashes = (parsesWithAccounts || []).map(({ parse }) => parse.fileHash).filter(Boolean);

		await this.update({
			verifiedStatementTransactions: [...this.state.verifiedStatementTransactions, ...transactions],
			verifiedStatementFileNames: [...this.state.verifiedStatementFileNames, ...verifiedFileNames],
			sessionStatementFileHashes: [...new Set([...this.state.sessionStatementFileHashes, ...sessionHashes])],
			statementVerificationGroups: null,
			pendingStatementDraft: null,
		});

		await this.fileStatementsInRegistry({ parsesWithAccounts });

		if (pendingDraft) this.proceedWithStatementDraft({ draft: pendingDraft });
	};

	fileStatementsInRegistry = async ({ parsesWithAccounts }) => {
		const registrationRows = BookkeepingService.buildStatementRegistrationRows({ parsesWithAccounts: parsesWithAccounts || [] });
		if (registrationRows.length === 0) return;

		const result = await BookkeepingService.registerStatements({ clientId: this.props.clientId, statements: registrationRows });
		if (!result || result.error) {
			alert.warning(`The imported statements could not be filed on the client's record (${(result && result.error) || "no response"}). The import itself is unaffected, but these statements won't be available to other fiscal years until they are imported again.`, { autoClose: 10000 });
			return;
		}

		await this.update({
			verifiedStatementTransactions: BookkeepingService.stampStatementIdOnTransactions({
				transactions: this.state.verifiedStatementTransactions,
				registrationResults: result.results || [],
			}),
		});
		await this.rememberStatementsPutBackOnTheShelf({ registrationResults: result.results || [] });

		const newlyFiledCount = (result.results || []).filter(row => !row.alreadyOnFile).length;
		if (newlyFiledCount > 0) {
			alert.info(`${newlyFiledCount} statement${newlyFiledCount === 1 ? "" : "s"} filed on the client's record for reuse across fiscal years.`, { autoClose: 6000 });
		}

		const refusals = (result.results || []).filter(row => row.refused);
		for (const refusal of refusals) {
			alert.error(refusal.refused, { autoClose: 15000 });
		}

		const standInsTakenDown = (result.results || []).map(row => row.stoodInFor).filter(Boolean);
		for (const standIn of standInsTakenDown) {
			alert.warning(
				`This period was entered by hand before. The statement read from the file replaces it, and the ${standIn.archivedTransactionCount} transaction${standIn.archivedTransactionCount === 1 ? "" : "s"} entered by hand moved to Archived so the month is not counted twice.`,
				{ autoClose: 12000 }
			);
		}
	};

	onStatementVerificationClose = () => {
		this.update({ statementVerificationGroups: null, pendingStatementDraft: null });
	};

	isFolderPickerSupported = () => typeof window.showDirectoryPicker === "function";

	onWizardCwUpload = async () => {
		let dirHandle;
		try {
			dirHandle = await window.showDirectoryPicker({ mode: "read" });
		} catch (e) {
			if (e.name !== "AbortError") console.error(e);
			return;
		}

		const cwFiles = [];
		for await (const [name, handle] of dirHandle) {
			if (handle.kind !== "file") continue;
			if (isCaseWareDataFile(name)) {
				cwFiles.push(await handle.getFile());
			}
		}

		if (cwFiles.length === 0) {
			alert.error("No CaseWare data files (AM.dbf, BL.dbf, …) found in that folder.");
			return;
		}

		await this.processCaseWareFiles({ cwFiles, folderName: dirHandle.name });
	};

	onCaseWareFileInputChange = async e => {
		const chosenFiles = Array.from(e.target.files || []);
		e.target.value = "";

		const cwFiles = chosenFiles.filter(file => isCaseWareDataFile(file.name));
		if (cwFiles.length === 0) {
			alert.error("None of the selected files are CaseWare data files (AM.dbf, BL.dbf, …).");
			return;
		}

		await this.processCaseWareFiles({ cwFiles, folderName: "" });
	};

	processCaseWareFiles = async ({ cwFiles, folderName }) => {
		const { clientId } = this.props;

		const amFile = cwFiles.find(file => fileNameEndsWith(file.name, "am.dbf"));
		const cwPriorYearEnd = amFile ? BookkeepingService.extractIsoDateFromFileName({ fileName: amFile.name }) : null;

		await this.update({ cwFiles, cwPriorYearEnd, bankAccountsFromCw: [], cwIdentifyLoading: true, cwSkipped: false });

		if (!this.state.industry && folderName) {
			const entityFromFolder = BookkeepingService.stripTrailingYearFromFolderName({ folderName });
			if (entityFromFolder) this.inferIndustryAsync(entityFromFolder);
		}

		try {
			const identifyFiles = cwFiles.filter(file => bankIdentificationFileSuffixes.some(suffix => fileNameEndsWith(file.name, suffix)));
			const result = await AFSService.identifyBankAccounts({ clientId, files: identifyFiles });
			if (result && result.bankAccounts) {
				let stateUpdate = { bankAccountsFromCw: result.bankAccounts, cwIdentifyLoading: false };
				const profileProvince = result.clientProfile ? BookkeepingService.getKnownProvinceOrEmpty({ province: result.clientProfile.province }) : "";
				if (profileProvince && !this.state.province) {
					stateUpdate.province = profileProvince;
				}
				await this.update(stateUpdate);
			} else {
				await this.update({ cwIdentifyLoading: false });
				alert.error("Could not identify bank accounts from the CaseWare data files.");
			}
		} catch (e) {
			console.error(e);
			await this.update({ cwIdentifyLoading: false });
			alert.error("Error reading CaseWare files.");
		}
	};

	onBankAccountStatementSelect = (accountNumber, file) => {
		const { bankAccountStatementMap } = this.state;
		this.update({ bankAccountStatementMap: { ...bankAccountStatementMap, [accountNumber]: file } });
		this.prepareStatementFile(file);
	};

	updateFileSign = (file, changes) => {
		const key = UtilityService.getFileKey(file);
		this.setState(prev => ({ statementSignByFile: UtilityService.mergeIntoKey({ object: prev.statementSignByFile, key, changes }) }));
	};

	setStatementColumns = (file, changes) => {
		const key = UtilityService.getFileKey(file);
		this.setState(prev => ({ statementColumnsByFile: UtilityService.mergeIntoKey({ object: prev.statementColumnsByFile, key, changes }) }));
	};

	prepareStatementFile = async (file) => {
		const readResult = await BookkeepingService.readStatementGrid({ clientId: this.props.clientId, file });
		if (readResult.error) {
			alert.error(readResult.error);
			return;
		}

		const { hasHeadings, headers, dataRows } = BookkeepingService.detectStatementHeadings({ grid: readResult.grid });
		const columnMap = BookkeepingService.guessStatementColumnMap({ headers, dataRows, hasHeadings });
		this.setStatementColumns(file, { headers, hasHeadings, columnMap });
		this.refreshStatementSign(file, columnMap);
	};

	updateStatementColumn = (file, field, columnIndex) => {
		const existingColumns = this.state.statementColumnsByFile[UtilityService.getFileKey(file)];
		if (!existingColumns) return;
		const columnMap = { ...existingColumns.columnMap, [field]: columnIndex };
		this.setStatementColumns(file, { columnMap });
		this.refreshStatementSign(file, columnMap);
	};

	refreshStatementSign = async (file, columnMap = null) => {
		const usesMoneyOutAndIn = !!columnMap && (columnMap.moneyOut >= 0 || columnMap.moneyIn >= 0);
		if (usesMoneyOutAndIn) {
			this.updateFileSign(file, { sampled: true, confirmed: true, inverted: false });
			return;
		}

		const { deposit, payment } = await BookkeepingService.sampleSignConvention({ file, clientId: this.props.clientId, columnMap });
		this.updateFileSign(file, { deposit, payment, sampled: true, confirmed: false, inverted: false });
	};

	confirmStatementSign = (file, inverted) => {
		this.updateFileSign(file, { confirmed: true, inverted });
	};

	onGlReferenceFileSelected = async (file) => {
		if (!file) return;
		let parsed = await BookkeepingService.readGeneralLedgerCsv({ file });
		if (!parsed) {
			alert.error("That CSV has no data rows — check the file and try again.");
			return;
		}
		this.update({ glReferenceFile: file, glReferenceHeaders: parsed.headers, glReferenceRows: parsed.rows, glReferenceColumnMap: parsed.columnMap });
	};

	onBankAccountStatementInputChange = (accountNumber, e) => {
		const file = e.target.files[0];
		e.target.value = "";
		if (file) this.onBankAccountStatementSelect(accountNumber, file);
	};

	updateGlReferenceColumn = (field, columnIndex) => {
		this.update({ glReferenceColumnMap: { ...this.state.glReferenceColumnMap, [field]: columnIndex } });
	};

	removeGlReferenceFile = () => {
		this.update({ ...emptyGlReferenceState(), glOpeningBalances: [], glOpeningReviewOpen: false, glOpeningReviewDone: false });
	};

	onWizardChange = e => { this.setState({ [e.target.name]: e.target.value }); };
	onWizardHasTaxToggle = () => this.update({ hasTax: !this.state.hasTax });

	inferIndustryAsync = async (entityName) => {
		try {
			let result = await BookkeepingService.inferIndustry({ entityName });
			if (result && result.industry && !this.state.industry) {
				await this.update({ industry: result.industry });
			}
		} catch (error) {
		}
	};


	renderWizardStep1 = () => {
		const { cwFiles, cwPriorYearEnd, bookingPeriod, bankAccountsFromCw, cwIdentifyLoading } = this.state;
		const caseWareFilesSelected = cwFiles.length > 0;
		const missingRequired = missingRequiredCaseWareFiles(cwFiles);
		const hasError = caseWareFilesSelected && missingRequired.length > 0;

		let priorYearLabel = null;
		let currentYearLabel = null;
		if (cwPriorYearEnd) {
			const priorYearEndDate = UtilityService.parseLocalDate(cwPriorYearEnd);
			const currentYearEndDate = new Date(priorYearEndDate);
			currentYearEndDate.setMonth(currentYearEndDate.getMonth() + bookingPeriod);
			priorYearLabel = UtilityService.formatShortDate(priorYearEndDate);
			currentYearLabel = UtilityService.formatShortDate(currentYearEndDate);
		}

		return (
			<div className="bk-wizard__step-content">
				<div className="bk-wizard__info-box">
					<p className="bk-wizard__step-description">
						Select your CaseWare client folder — or select all the .dbf files inside it. We'll pick out the ones we need and read the chart of accounts and bank accounts.
					</p>
					<p className="bk-wizard__step-description">Typically found at:</p>
					<div className="bk-wizard__code-block">C:\Program Files (x86)\CaseWare\Data\ClientName\</div>
				</div>

				<div className="bk-wizard__upload-options">
					{this.isFolderPickerSupported() && (
						<button className={clsx("bk-wizard__upload-btn", caseWareFilesSelected && "bk-wizard__upload-btn--selected")} onClick={this.onWizardCwUpload}>
							<Icon.Folder size={15} />
							Select CaseWare Folder
						</button>
					)}
					<label className="bk-wizard__file-input-label">
						<div className={clsx("bk-wizard__upload-btn", caseWareFilesSelected && "bk-wizard__upload-btn--selected")}>
							<Icon.File size={15} />
							Select .dbf Files
						</div>
						<input
							type="file"
							accept=".dbf"
							multiple
							className="bk-wizard__file-input-hidden"
							onChange={this.onCaseWareFileInputChange}
						/>
					</label>
				</div>

				{(caseWareFilesSelected || this.isFolderPickerSupported()) && (
					<p className="bk-wizard__step-description bk-wizard__step-description--muted">
						{caseWareFilesSelected
							? `${cwFiles.length} files selected — select again to change.`
							: "Browsers can't open folders inside Program Files — use Select .dbf Files for that location."}
					</p>
				)}

				{cwIdentifyLoading && (
					<div className="bk-wizard__loading">
						<Loader size="small" />
						<span>Reading bank accounts…</span>
					</div>
				)}

				{hasError && (
					<div className="bk-wizard__error-box">
						<strong>Missing required files:</strong> {missingRequired.join(", ")}. AM.dbf and BL.dbf must be present.
					</div>
				)}

				{!cwIdentifyLoading && bankAccountsFromCw.length > 0 && (
					<div className="bk-wizard__success-box">
						<strong>{bankAccountsFromCw.length} bank account{bankAccountsFromCw.length > 1 ? "s" : ""} identified</strong> from CaseWare.
						Upload a statement for each in the next step.
					</div>
				)}

				{!cwIdentifyLoading && caseWareFilesSelected && !hasError && bankAccountsFromCw.length === 0 && (
					<div className="bk-wizard__warning-box">
						No bank accounts found in this CaseWare file (no SCHEDULE='A' posting accounts).
					</div>
				)}

				{cwPriorYearEnd && (
					<div className="bk-wizard__year-detected">
						<div className="bk-wizard__year-detected-title">Year Detected</div>
						<div className="bk-wizard__year-grid">
							<div>
								<div className="bk-wizard__year-label">Prior Year End</div>
								<div className="bk-wizard__year-value">{priorYearLabel}</div>
							</div>
							<div>
								<div className="bk-wizard__year-label">Current Year End</div>
								<div className="bk-wizard__year-value bk-wizard__year-value--current">{currentYearLabel}</div>
							</div>
						</div>
						<div className="bk-wizard__period-label">Bookkeeping period:</div>
						<div className="bk-wizard__period-options">
							{[1, 3, 6, 12].map(p => (
								<label key={p} className="bk-wizard__period-option">
									<input
										type="radio"
										name="bookingPeriod"
										value={p}
										checked={bookingPeriod === p}
										onChange={() => this.update({ bookingPeriod: p })}
									/>
									{p === 12 ? "12 months (annual)" : `${p} month${p > 1 ? "s" : ""}`}
								</label>
							))}
						</div>
					</div>
				)}
			</div>
		);
	};

	renderStatementColumnPrompt = file => {
		const columns = this.state.statementColumnsByFile[UtilityService.getFileKey(file)];
		if (!columns) return null;

		const { headers, hasHeadings, columnMap } = columns;
		const headingsLabel = hasHeadings ? "Heading row detected — the first row is treated as labels" : "No heading row detected — every row is imported as a transaction";
		const needsDateOrDescription = columnMap.date < 0 || columnMap.description < 0;
		const needsAnAmount = columnMap.amount < 0 && columnMap.moneyOut < 0 && columnMap.moneyIn < 0;

		return (
			<div className="bk-wizard__statement-columns">
				<div className="bk-wizard__statement-columns-head">
					<span className="bk-wizard__statement-columns-title">Columns in {file.name}</span>
					<span className="bk-wizard__statement-columns-headings">{headingsLabel}</span>
				</div>
				<div className="bk-wizard__gl-colmap-grid">
					<ColSelect label="Date (required)" value={columnMap.date} onChange={v => this.updateStatementColumn(file, "date", v)} headers={headers} />
					<ColSelect label="Description (required)" value={columnMap.description} onChange={v => this.updateStatementColumn(file, "description", v)} headers={headers} />
					<ColSelect label="Amount (single column)" value={columnMap.amount} onChange={v => this.updateStatementColumn(file, "amount", v)} headers={headers} />
					<ColSelect label="Money out (withdrawal)" value={columnMap.moneyOut} onChange={v => this.updateStatementColumn(file, "moneyOut", v)} headers={headers} />
					<ColSelect label="Money in (deposit)" value={columnMap.moneyIn} onChange={v => this.updateStatementColumn(file, "moneyIn", v)} headers={headers} />
				</div>
				{(needsDateOrDescription || needsAnAmount) && (
					<p className="bk-wizard__gl-colmap-hint">
						{needsDateOrDescription ? "Select a Date and a Description column." : "Select an Amount column, or a Money out and Money in pair."}
					</p>
				)}
			</div>
		);
	};

	renderStatementFilePrompts = (file, index) => (
		<div key={file.name + index}>
			{this.renderStatementColumnPrompt(file)}
			{this.renderStatementSignPrompt(file, index)}
		</div>
	);

	renderStatementSignPrompt = (file, index) => {
		const sign = this.state.statementSignByFile[UtilityService.getFileKey(file)];
		if (!sign || !sign.sampled || sign.confirmed) return null;

		const question = sign.deposit ? `Is "${sign.deposit.description}" a deposit (money in)?` : "Are positive amounts deposits (money in)?";

		return (
			<div key={file.name + index} className="crw__sign-confirm">
				<p className="crw__sign-confirm-title">Confirm the sign convention for <strong>{file.name}</strong>:</p>
				{(sign.deposit || sign.payment) && (
					<div className="crw__sign-confirm-preview">
						{sign.deposit && (
							<div className="crw__sign-confirm-row">
								<span className="crw__sign-confirm-desc">{sign.deposit.description}</span>
								<span className="crw__sign-confirm-amount crw__sign-confirm-amount--positive">${Math.abs(sign.deposit.amount).toLocaleString("en-CA", { minimumFractionDigits: 2 })}</span>
							</div>
						)}
						{sign.payment && (
							<div className="crw__sign-confirm-row">
								<span className="crw__sign-confirm-desc">{sign.payment.description}</span>
								<span className="crw__sign-confirm-amount crw__sign-confirm-amount--negative">-${Math.abs(sign.payment.amount).toLocaleString("en-CA", { minimumFractionDigits: 2 })}</span>
							</div>
						)}
					</div>
				)}
				<p className="crw__sign-confirm-question">{question}</p>
				<div className="crw__sign-confirm-buttons">
					<button className="crw__sign-confirm-btn crw__sign-confirm-btn--yes" onClick={() => this.confirmStatementSign(file, false)}>
						Yes — positive = deposits
					</button>
					<button className="crw__sign-confirm-btn crw__sign-confirm-btn--no" onClick={() => this.confirmStatementSign(file, true)}>
						No — positive = payments (invert signs)
					</button>
				</div>
			</div>
		);
	};

	renderCaseWareStatementsStep = () => {
		const { bankAccountsFromCw, bankAccountStatementMap } = this.state;

		return (
			<div className="bk-wizard__step-content bk-wizard__step-content--step2">
				<div className="bk-wizard__info-box">
					<div className="bk-wizard__info-box-title">Upload Bank Statements</div>
					<p className="bk-wizard__step-description">
						Upload a CSV or Excel statement for each account — we detect the columns and let you confirm them before importing. You can skip accounts that are inactive this period.
					</p>
				</div>

				{bankAccountsFromCw.map(acct => {
					const file = bankAccountStatementMap[acct.accountNumber];
					const isPositive = acct.openingBalance >= 0;

					return (
						<div key={acct.accountNumber} className="bk-wizard__account-card">
							<div className="bk-wizard__account-card-header">
								<div className="bk-wizard__account-card-header-left">
									<div>
										<div className="bk-wizard__account-num">{acct.accountNumber}</div>
										<div className="bk-wizard__account-desc">{acct.description}</div>
									</div>
								</div>
								<div>
									<div className="bk-wizard__opening-bal-label">Opening Balance</div>
									<div className={clsx(isPositive ? "bk-wizard__opening-bal--positive" : "bk-wizard__opening-bal--negative")}>{formatOpeningBalance(acct.openingBalance)}</div>
								</div>
							</div>
							<div className="bk-wizard__account-card-body">
								<label className="bk-wizard__file-input-label">
									<div className={clsx("bk-wizard__upload-btn", !!file && "bk-wizard__upload-btn--selected")}>
										<Icon.Upload size={14} />
										{file ? file.name : "Select statement…"}
									</div>
									<input
										type="file"
										accept=".csv,.xlsx"
										className="bk-wizard__file-input-hidden"
										onChange={e => this.onBankAccountStatementInputChange(acct.accountNumber, e)}
									/>
								</label>
								{file && this.renderStatementColumnPrompt(file)}
							</div>
						</div>
					);
				})}
			</div>
		);
	};

	onPeriodDraftChange = ({ periodEnd, periodStart, lengthMonths }) => {
		const changes = { periodDraftError: "" };
		if (periodEnd !== undefined) changes.periodDraftYearEnd = periodEnd;
		if (periodStart !== undefined) changes.periodDraftFirstDay = periodStart;
		if (lengthMonths !== undefined) changes.periodDraftLengthMonths = lengthMonths;
		this.update(changes);
	};

	derivePeriodDraft = () => {
		const { periodDraftYearEnd, periodDraftLengthMonths, periodDraftFirstDay } = this.state;
		if (!periodDraftYearEnd) return null;
		const derived = BookkeepingService.derivePeriodFromYearEnd({
			fiscalYearEnd: periodDraftYearEnd,
			periodLengthMonths: periodDraftLengthMonths,
			periodStartOverride: periodDraftFirstDay,
		});
		return derived;
	};




	renderAddingToBooksBanner = () => {
		const existingTransactions = this.state.transactionsBeforeImport;
		if (!existingTransactions || existingTransactions.length === 0) return null;

		return (
			<div className="bk-wizard__adding-banner">
				<Icon.PlusCircle size={14} />
				<span>
					Adding to the <strong>{existingTransactions.length} transactions</strong> already in these books. Nothing already imported is replaced, and anything that is already here will not be added twice.
				</span>
			</div>
		);
	};

	renderFiscalPeriodStep = () => {
		const { periodDraftYearEnd, periodDraftLengthMonths, periodDraftFirstDay, periodDraftError } = this.state;
		const needsItsOwnStart = periodDraftLengthMonths === CUSTOM_PERIOD_LENGTH;

		return (
			<div className="bk-wizard__step-content">
				<FiscalPeriodFields
					radioGroupName="bk-wizard-period-length"
					periodEnd={periodDraftYearEnd}
					periodStart={periodDraftFirstDay}
					lengthMonths={periodDraftLengthMonths}
					errorText={periodDraftError}
					onDraftChange={this.onPeriodDraftChange}
				/>

				<div>
					<Button
						text="Confirm fiscal period"
						onClick={this.onConfirmFiscalPeriod}
						disabled={!periodDraftYearEnd || (needsItsOwnStart && !periodDraftFirstDay)}
					/>
				</div>
			</div>
		);
	};

	renderFiscalPeriodChip = () => {
		const { fiscalPeriod, periodConfirmed } = this.state;
		if (!periodConfirmed || !fiscalPeriod) return null;
		const label = `FY ${moment(fiscalPeriod.periodStart).format("MMM D, YYYY")} – ${moment(fiscalPeriod.periodEnd).format("MMM D, YYYY")}`;
		return (
			<button type="button" className="bk-wizard__period-chip" title="Change the fiscal period" onClick={this.onChangeFiscalPeriod}>
				{label}
			</button>
		);
	};

	renderModeChooser = () => {
		const modes = [
			{ key: "gl", icon: Icon.FileText, title: "Start from a General Ledger", description: "Import a GL export — we'll flag the accounts that look like bank accounts for you to confirm.", recommended: true },
			{ key: "caseware", icon: Icon.Folder, title: "Start from a CaseWare client file (.dbf)", description: "Read the chart of accounts and bank accounts straight from your CaseWare client file. To upload a CaseWare general ledger saved as Excel instead, choose \"Start from a General Ledger\" above." },
			{ key: "manual", icon: Icon.Upload, title: "Just upload statements", description: "No prior general ledger to work from — best for first-year engagements. Name each bank account and attach its CSV, Excel or PDF statements." },
		];

		return (
			<div className="bk-wizard__step-content">
				<p className="bk-wizard__step-description">How do you want to start this engagement?</p>
				<div className="bk-wizard__mode-list">
					{modes.map(mode => (
						<button key={mode.key} className="bk-wizard__mode-card" onClick={() => this.onChooseImportMode(mode.key)}>
							<mode.icon size={22} className="bk-wizard__mode-card-icon" />
							<div className="bk-wizard__mode-card-text">
								<div className="bk-wizard__mode-card-title">
									{mode.title}
									{mode.recommended && <span className="bk-wizard__recommended-pill">Recommended for accuracy</span>}
								</div>
								<div className="bk-wizard__mode-card-desc">{mode.description}</div>
							</div>
							<Icon.ChevronRight size={18} className="bk-wizard__mode-card-chevron" />
						</button>
					))}
				</div>
			</div>
		);
	};

	renderGlSourceIcon = sourceKey => {
		if (sourceKey === "general") return <Icon.FileText size={22} className="bk-wizard__mode-card-icon" />;
		if (sourceKey === "caseware") return <CaseWareGlyph className="bk-wizard__brand-icon" />;
		if (sourceKey === "xero") return <XeroGlyph className="bk-wizard__brand-icon" />;
		return <QuickBooksGlyph className="bk-wizard__brand-icon" />;
	};

	renderGlSourceCard = source => {
		return (
			<button
				key={source.key}
				className={clsx("bk-wizard__mode-card", !source.available && "bk-wizard__mode-card--soon")}
				onClick={() => this.onChooseGlSource(source)}
			>
				{this.renderGlSourceIcon(source.key)}
				<div className="bk-wizard__mode-card-text">
					<div className="bk-wizard__mode-card-title">
						{source.title}
						{!source.available && <span className="bk-wizard__soon-pill">Coming soon</span>}
					</div>
					<div className="bk-wizard__mode-card-desc">{source.description}</div>
				</div>
				{source.available && <Icon.ChevronRight size={18} className="bk-wizard__mode-card-chevron" />}
			</button>
		);
	};

	renderGlSourcePicker = () => {
		const sources = [
			{ key: "general", title: "General (CSV)", description: "Upload a general ledger export as a CSV and map the columns yourself.", available: true },
			{ key: "caseware", title: "CaseWare (Excel)", description: "Upload a CaseWare general ledger Excel export — we read the accounts, entries, and opening balances.", available: true },
			{ key: "xero", title: "Xero (Excel)", description: "Upload a Xero General Ledger Detail Excel export — we read the accounts, entries, and opening balances.", available: true },
			{ key: "quickbooks", title: "QuickBooks (Excel)", description: "Upload a QuickBooks Online general ledger Excel export — we read the accounts, entries, and opening balances.", available: true },
		];

		return (
			<div className="bk-wizard__step-content">
				<p className="bk-wizard__step-description">Where is the general ledger coming from?</p>
				<div className="bk-wizard__mode-list">
					{sources.map(this.renderGlSourceCard)}
				</div>
			</div>
		);
	};

	renderBankAccountReview = () => {
		const { importMode, glBankDetecting, bankAccountsLoading, confirmedBankAccounts, glDetectedBankAccounts, glAccountList, bankAccountsOnTheFile, chartAccountsForBankPicker } = this.state;
		const startsFromAGeneralLedger = importMode === "gl";

		if (glBankDetecting || bankAccountsLoading) {
			return (
				<div className="bk-wizard__loading">
					<Loader size="small" />
					<span>{startsFromAGeneralLedger ? "Finding the likely bank accounts…" : "Reading the bank accounts on this file…"}</span>
				</div>
			);
		}

		const accountsOnTheFile = startsFromAGeneralLedger ? [] : bankAccountsOnTheFile.filter(account => account.isOnTheFile);
		const namesOnlyOnTheShelf = bankAccountsOnTheFile.filter(account => !account.isOnTheFile).map(account => account.name);
		const namesToStartWith = startsFromAGeneralLedger ? glDetectedBankAccounts : namesOnlyOnTheShelf;
		const detectedNames = confirmedBankAccounts.length > 0 ? confirmedBankAccounts : namesToStartWith;
		const allAccountNames = startsFromAGeneralLedger ? glAccountList : BookkeepingService.listChartAccountNames({ chartAccounts: chartAccountsForBankPicker });
		const chartAccountOptions = BookkeepingService.listChartAccountOptions({ clientChart: chartAccountsForBankPicker });

		return (
			<BankAccountReview
				accountsOnTheFile={accountsOnTheFile}
				statementsByAccount={this.state.statementsOnTheShelfByAccount}
				transactionCountByAccount={BookkeepingService.countTransactionsByBankAccount({ transactions: this.state.transactions })}
				onChangeCurrency={this.onChangeBankAccountCurrency}
				detectedNames={detectedNames}
				allAccountNames={allAccountNames}
				chartAccountOptions={chartAccountOptions}
				pickerSource={startsFromAGeneralLedger ? "general ledger" : "chart of accounts"}
				isAddingToExistingBooks={this.isAddingToExistingBooks()}
				initialFileByAccount={this.state.fileByBankAccount}
				initialCurrencyByAccount={this.state.currencyByBankAccount}
				onDraftChange={this.onBankReviewDraftChange}
				onTieAccount={this.onTieBankAccountToChart}
				renderFilePrompts={file => this.renderStatementFilePrompts(file, 0)}
			/>
		);
	};

	renderGlExcelReadSummary = ({ excelSourceLabel }) => {
		const { glReferenceRows, glOpeningBalances, glLayout, glEntityName } = this.state;
		const tieOut = BookkeepingService.summarizeOpeningBalanceTieOut({ openingBalances: glOpeningBalances });
		const layoutLabel = GL_LAYOUT_LABELS[glLayout] || "";
		const entryCountLabel = `${glReferenceRows.length} ${glReferenceRows.length === 1 ? "entry" : "entries"}`;
		const openingCountLabel = `${glOpeningBalances.length} opening balance${glOpeningBalances.length === 1 ? "" : "s"}`;
		const hasOpeningBalances = glOpeningBalances.length > 0;
		const showsTieOutWarning = hasOpeningBalances && !tieOut.isBalanced;
		const summaryClassName = showsTieOutWarning ? "bk-wizard__gl-read bk-wizard__gl-read--warning" : "bk-wizard__gl-read";
		const differenceLabel = UtilityService.formatCurrency(Math.abs(tieOut.difference));
		const heavierSideLabel = tieOut.difference > 0 ? "debits" : "credits";

		return (
			<div className={summaryClassName}>
				<div className="bk-wizard__gl-read-headline">
					{entryCountLabel} and {openingCountLabel} read from {excelSourceLabel}
				</div>
				<div className="bk-wizard__gl-read-meta">
					{glEntityName && <span>{glEntityName}</span>}
					{layoutLabel && <span>Read from the {layoutLabel}</span>}
				</div>
				{showsTieOutWarning && (
					<div className="bk-wizard__gl-read-exception">
						<Icon.AlertTriangle size={14} className="bk-wizard__gl-read-exception-icon" />
						<span>
							These opening balances don't balance — {heavierSideLabel} are higher by <strong>{differenceLabel}</strong>. You can still continue; review them on the next step and post the difference before finalizing.
						</span>
					</div>
				)}
				{!showsTieOutWarning && hasOpeningBalances && (
					<div className="bk-wizard__gl-read-checked">
						<Icon.Check size={14} className="bk-wizard__gl-read-checked-icon" />
						<span>Opening balances balance.</span>
					</div>
				)}
			</div>
		);
	};

	renderWizardGlReference = () => {
		const { glReferenceFile, glReferenceHeaders, glReferenceColumnMap, importMode } = this.state;
		const columnMap = glReferenceColumnMap;
		const isGlFirst = importMode === "gl";
		const needsRequiredColumns = columnMap.account < 0 || columnMap.description < 0;

		const isExcelSource = EXCEL_GL_SOURCES.includes(this.state.glSource);
		const excelSourceLabel = EXCEL_GL_SOURCE_LABELS[this.state.glSource] || "CaseWare";

		return (
			<div className="bk-wizard__step-content">
				<div className="bk-wizard__info-box">
					<div className="bk-wizard__info-box-title">{isExcelSource ? `Import from ${excelSourceLabel}` : isGlFirst ? "Import your general ledger" : "General Ledger Reference (optional)"}</div>
					<p className="bk-wizard__step-description">
						{isExcelSource ? (
							<>
								Upload your <strong>{excelSourceLabel} general ledger Excel export</strong> (the General Ledger report saved as .xlsx). We read the accounts and entries to detect your bank accounts and categorize the statement transactions, and pull in each account's <strong>opening balance</strong> for you to review.
							</>
						) : isGlFirst ? (
							<>
								Upload your <strong>general ledger export (CSV)</strong>. We read the account list to detect your bank accounts, and use the entries to auto-categorize the statement transactions later. Map at least the <strong>Account</strong> and <strong>Description</strong> columns — mapping <strong>Tax</strong> also sets each account's default tax code.
							</>
						) : (
							<>
								Upload a prior <strong>general ledger export (CSV)</strong> to auto-categorize these transactions by matching their descriptions against how they were booked before. It needs at least an <strong>Account</strong> column and a <strong>Description</strong> column — map the <strong>Tax</strong> column too and each account's default tax code is derived from it. Skip this step if you don't have one.
							</>
						)}
					</p>
				</div>

				{!glReferenceFile && isExcelSource && (
					<FileDropZone
						className="bk-wizard__gl-dropzone"
						accept=".xlsx"
						onFiles={files => this.onGlExcelFileSelected(this.state.glSource, files[0])}
						title={`Select your ${excelSourceLabel} GL export`}
						subtitle="The General Ledger report saved as Excel (.xlsx)"
					/>
				)}

				{!glReferenceFile && !isExcelSource && (
					<FileDropZone
						className="bk-wizard__gl-dropzone"
						accept=".csv"
						onFiles={files => this.onGlReferenceFileSelected(files[0])}
						title="Select your general ledger CSV"
						subtitle="Exported from CaseWare, Xero, QuickBooks, or any accounting system"
					/>
				)}

				{glReferenceFile && (
					<>
						<div className="bk-wizard__file-list">
							<div className="bk-wizard__file-row">
								<Icon.FileText size={14} className="bk-wizard__file-row-icon" />
								<span className="bk-wizard__file-row-name">{glReferenceFile.name}</span>
								<button type="button" className="bk-wizard__file-remove" title="Remove this file" onClick={this.removeGlReferenceFile}>
									<Icon.X size={14} />
								</button>
							</div>
						</div>
						{isExcelSource ? (
							this.renderGlExcelReadSummary({ excelSourceLabel })
						) : (
							<div className="bk-wizard__gl-colmap">
								<div className="bk-wizard__gl-colmap-title">Map your columns</div>
								<div className="bk-wizard__gl-colmap-grid">
									<ColSelect label="Account (required)" value={columnMap.account} onChange={v => this.updateGlReferenceColumn("account", v)} headers={glReferenceHeaders} />
									<ColSelect label="Account # (optional)" value={columnMap.accountNumber} onChange={v => this.updateGlReferenceColumn("accountNumber", v)} headers={glReferenceHeaders} />
									<ColSelect label="Description (required)" value={columnMap.description} onChange={v => this.updateGlReferenceColumn("description", v)} headers={glReferenceHeaders} />
									<ColSelect label="Date" value={columnMap.date} onChange={v => this.updateGlReferenceColumn("date", v)} headers={glReferenceHeaders} />
									<ColSelect label="Debit" value={columnMap.debit} onChange={v => this.updateGlReferenceColumn("debit", v)} headers={glReferenceHeaders} />
									<ColSelect label="Credit" value={columnMap.credit} onChange={v => this.updateGlReferenceColumn("credit", v)} headers={glReferenceHeaders} />
									<ColSelect label="Tax" value={columnMap.tax} onChange={v => this.updateGlReferenceColumn("tax", v)} headers={glReferenceHeaders} />
								</div>
								{needsRequiredColumns && (
									<p className="bk-wizard__gl-colmap-hint">Select an Account and a Description column to continue.</p>
								)}
							</div>
						)}
					</>
				)}
			</div>
		);
	};

	renderWizardStep3 = () => {
		const { industry, clientIndustry, hasTax, startDate, endDate, connectedAccounts } = this.state;
		const needsDates = connectedAccounts.some(a => a.isChecked);
		const selectedIndustry = toCanonicalIndustry(industry) || toCanonicalIndustry(clientIndustry);
		const knownProvince = BookkeepingService.getKnownProvinceOrEmpty({ province: this.state.province });

		return (
			<div className="bk-wizard__step-content bk-wizard__step-content--step3">
				<p className="bk-wizard__step-description">
					These settings drive automatic transaction classification. You can adjust them later in the table header.
				</p>
				<div>
					<label className="bk-wizard__field-label">Industry</label>
					<ToolsSelect
						value={selectedIndustry}
						options={INDUSTRY_ENUM.map(name => ({ value: name, label: name }))}
						onChange={name => this.update({ industry: name })}
						placeholder="Select an industry…"
						searchable
						searchPlaceholder="Search industries…"
					/>
				</div>
				{knownProvince ? (
					<div>
						<label className="bk-wizard__field-label">Province</label>
						<div className="bk-wizard__detected-row">
							<span className="bk-wizard__detected-value">{knownProvince}</span>
							<button className="bk-wizard__change-btn" onClick={() => this.update({ province: "" })}>Change</button>
						</div>
					</div>
				) : (
					<div>
						<label className="bk-wizard__field-label">Province</label>
						<ToolsSelect
							value=""
							options={BookkeepingService.getProvinceOptions()}
							onChange={name => this.update({ province: name })}
							placeholder="Select a province…"
							searchable
							searchPlaceholder="Search provinces…"
						/>
					</div>
				)}
				{this.state.glReferenceFile && (
					<div>
						<label className="bk-wizard__field-label">Tax rates</label>
						<p className="bk-wizard__step-description bk-wizard__step-description--muted">
							Taken from your general ledger — each account's rate is reverse-engineered from its booked tax and net amounts.
						</p>
					</div>
				)}
				<Checkbox title="Taxes applicable" checked={hasTax} onChange={this.onWizardHasTaxToggle} />
				{needsDates && (
					<>
						<DateSelector title="Start Date" selectedDate={startDate ? new Date(startDate) : undefined} onDateSelect={this.onStartDateChange} />
						<DateSelector title="End Date" selectedDate={endDate ? new Date(endDate) : undefined} onDateSelect={this.onEndDateChange} />
					</>
				)}
			</div>
		);
	};

	renderStepIndicator = (currentStep) => {
		const { importMode } = this.state;
		if (importMode === "") return null;

		const bootstrap = this.state.isBootstrapFile && !!this.state.wpfEngagementId;

		let steps;
		let displayStep;
		if (importMode === "gl") {
			steps = ["General Ledger", "Bank Accounts & Statements", "Settings"];
			displayStep = currentStep;
		} else if (importMode === "manual") {
			if (this.isAddingToExistingBooks()) return null;
			steps = ["Bank Accounts & Statements", "GL Reference", "Settings"];
			displayStep = currentStep - 1;
		} else {
			steps = ["CaseWare Data", "Bank Statements", "Settings"];
			displayStep = currentStep;
		}

		if (bootstrap && importMode !== "gl") {
			steps = ["Opening Balances", ...steps];
			displayStep = this.state.openingBalancesDone ? displayStep + 1 : 1;
		}
		return (
			<div>
				<div className="bk-wizard__step-indicator">
					{steps.map((label, i) => {
						const num = i + 1;
						const active = num === displayStep;
						const done = num < displayStep;
						return (
							<React.Fragment key={num}>
								{i > 0 && <div className={clsx("bk-wizard__step-line", done && "bk-wizard__step-line--done")} />}
								<div className="bk-wizard__step-column">
									<div className={clsx("bk-wizard__step-dot", (active || done) && "bk-wizard__step-dot--active")}>
										{done ? <Icon.Check size={13} /> : num}
									</div>
								</div>
							</React.Fragment>
						);
					})}
				</div>
				<div className="bk-wizard__step-labels">
					{steps.map((label, i) => {
						const num = i + 1;
						const active = num === displayStep;
						const align = i === 0 ? "left" : i === steps.length - 1 ? "right" : "center";
						return (
							<div key={num} className={clsx("bk-wizard__step-label-cell", "bk-wizard__step-label-cell--" + align)}>
								<span className={clsx("bk-wizard__step-label-text", active && "bk-wizard__step-label-text--active")}>{label}</span>
							</div>
						);
					})}
				</div>
			</div>
		);
	};

	onStep1NextClicked = () => {
		const { cwSkipped, bankAccountsFromCw } = this.state;
		if (!cwSkipped && bankAccountsFromCw.length > 0) {
			this.update({ coaMappingOpen: true });
		} else {
			this.onWizardNext();
		}
	};

	renderCaseWareStatementsNextButton = () => <Button text="Next" onClick={this.onWizardNext} />;

	renderGlReferenceNextButton = () => {
		const { glReferenceFile, glReferenceColumnMap } = this.state;
		const glIncomplete = !!glReferenceFile && (glReferenceColumnMap.account < 0 || glReferenceColumnMap.description < 0);

		return (
			<Button
				text="Next"
				onClick={this.onWizardNext}
				disabled={glIncomplete}
				title={glIncomplete ? "Select an Account and Description column, or remove the GL file to skip" : undefined}
			/>
		);
	};

	renderGenerateButton = () => {
		const knownProvince = BookkeepingService.getKnownProvinceOrEmpty({ province: this.state.province });

		return (
			<Button
				text={this.state.loading ? "Generating…" : "Generate Bookkeeping"}
				onClick={this.onGenerateBookkeepingClicked}
				disabled={this.state.loading || !knownProvince}
				title={!knownProvince ? "Select the province first — transactions cannot be classified without it" : undefined}
			/>
		);
	};

	renderGlImportNextButton = () => {
		const { glReferenceFile, glReferenceColumnMap, glBankDetecting } = this.state;
		const isReady = !!glReferenceFile && glReferenceColumnMap.account >= 0 && glReferenceColumnMap.description >= 0;

		return (
			<Button
				text={glBankDetecting ? "Working…" : "Next"}
				onClick={this.onGlStepNext}
				disabled={!isReady || glBankDetecting}
				title={!isReady ? "Upload a general ledger and map the Account and Description columns" : undefined}
			/>
		);
	};

	renderWizardBackButton = openingPhase => {
		const { importMode, wizardStep, coaMappingOpen, glSource } = this.state;
		if (openingPhase) return null;
		if (importMode === "") return null;
		if (coaMappingOpen) return null;

		const isAtFirstAddStep = this.isAddingToExistingBooks() && importMode === "manual" && wizardStep === 2;
		if (isAtFirstAddStep) return null;

		let onBack;
		if (importMode === "gl" && wizardStep === 1 && this.state.glOpeningReviewOpen) {
			onBack = this.onGlSourceBack;
		} else if (importMode === "gl" && wizardStep === 1) {
			onBack = glSource ? this.onGlSourceBack : this.onBackToModeChooser;
		} else {
			const atModeStart =
				(importMode === "caseware" && wizardStep === 1) ||
				(importMode === "manual" && wizardStep === 2);
			onBack = atModeStart ? this.onBackToModeChooser : this.onWizardBack;
		}
		return <Button text="Back" view="secondary" onClick={onBack} />;
	};

	getWizardTitle = (openingPhase, periodGateOpen) => {
		const { importMode, wizardStep, coaMappingOpen, glSource } = this.state;
		if (periodGateOpen) return "Set the Fiscal Period";
		if (openingPhase) return "Enter Opening Balances";
		if (importMode === "") return "Start Bookkeeping";

		if (importMode === "gl") {
			if (wizardStep === 1 && this.state.glOpeningReviewOpen) return "Review Opening Balances";
			if (wizardStep === 1 && EXCEL_GL_SOURCE_LABELS[glSource]) return `Import from ${EXCEL_GL_SOURCE_LABELS[glSource]}`;
			if (wizardStep === 1) return glSource ? "Import General Ledger" : "Select GL Source";
			if (wizardStep === 2) return "Confirm Accounts & Upload Statements";
			return "Configure Settings";
		}
		if (importMode === "manual") {
			if (wizardStep === 2) return this.isAddingToExistingBooks() ? "Add Bank Statements" : "Confirm Accounts & Upload Statements";
			if (wizardStep === 3) return this.isAddingToExistingBooks() ? "Add a General Ledger Reference" : "General Ledger Reference";
			return "Configure Settings";
		}
		if (wizardStep === 1) return coaMappingOpen ? "Map Your Accounts" : "Import CaseWare Data";
		if (wizardStep === 2) return "Upload Bank Statements";
		return "Configure Settings";
	};

	renderWizard = () => {
		const { wizardOpen, wizardStep, bankAccountsFromCw, cwFiles, cwIdentifyLoading, importMode, coaMappingOpen, isBootstrapFile, openingBalancesDone, wpfEngagementId } = this.state;

		const periodGateOpen = !this.state.periodConfirmed;
		const openingPhase = wizardStep === 1 && !coaMappingOpen && isBootstrapFile && !!wpfEngagementId && !openingBalancesDone && importMode !== "" && importMode !== "gl";

		const missingRequired = missingRequiredCaseWareFiles(cwFiles);
		const canProceedStep1 = bankAccountsFromCw.length > 0 && missingRequired.length === 0 && !cwIdentifyLoading;

		const casewareMode = importMode === "caseware";
		const manualMode = importMode === "manual";
		const glMode = importMode === "gl";
		const glSourceChosen = this.state.glSource !== "";
		const glOpeningReviewOpen = this.state.glOpeningReviewOpen;

		return (
			<Modal
				classNames={{ modal: "bk-wizard__modal" }}
				open={wizardOpen}
				onClose={this.onWizardClose}
				center
			>
				<div className="bk-wizard__modal-title">
					<div className="bk-wizard__modal-title-row">
						<h2 className="bk-wizard__title-text">{this.getWizardTitle(openingPhase, periodGateOpen)}</h2>
						{this.renderFiscalPeriodChip()}
					</div>
					<div className="bk-wizard__footer-spacer">
						{!periodGateOpen && this.renderStepIndicator(wizardStep)}
					</div>
				</div>

				<div className="modal-content bk-wizard__modal-scrollable">
					{this.renderAddingToBooksBanner()}
					{periodGateOpen && this.renderFiscalPeriodStep()}
					{!periodGateOpen && (
					<React.Fragment>
					{openingPhase && (
						<OpeningBalanceEntry
							clientId={this.props.clientId}
							wpfEngagementId={wpfEngagementId}
							contextError={this.state.engagementContextError}
							source="manual"
							onComplete={() => this.update({ openingBalancesDone: true })}
						/>
					)}
					{!openingPhase && importMode === "" && wizardStep === 1 && this.renderModeChooser()}

					{glMode && wizardStep === 1 && !glSourceChosen && this.renderGlSourcePicker()}
					{glMode && wizardStep === 1 && glSourceChosen && !glOpeningReviewOpen && this.renderWizardGlReference()}
					{glMode && wizardStep === 1 && glSourceChosen && glOpeningReviewOpen && (
						<OpeningBalanceEntry
							clientId={this.props.clientId}
							wpfEngagementId={wpfEngagementId}
							contextError={this.state.engagementContextError}
							source="excel_tb"
							prefillLines={this.state.glOpeningBalances}
							onComplete={this.onGlOpeningReviewDone}
						/>
					)}
					{glMode && wizardStep === 2 && this.renderBankAccountReview()}
					{glMode && wizardStep === 3 && this.renderWizardStep3()}

					{!openingPhase && casewareMode && wizardStep === 1 && !coaMappingOpen && this.renderWizardStep1()}
					{casewareMode && wizardStep === 1 && coaMappingOpen && (
						<CoaMappingReview
							clientId={this.props.clientId}
							accounts={this.state.bankAccountsFromCw.map(a => ({
								accountNumber: a.accountNumber,
								accountName: a.description || a.accountDescription,
								gifiCode: a.gifi || a.gifiCode || null,
							}))}
							onComplete={() => { this.update({ coaMappingOpen: false }); this.onWizardNext(); }}
							onSkip={() => { this.update({ coaMappingOpen: false }); this.onWizardNext(); }}
						/>
					)}
					{casewareMode && wizardStep === 2 && this.renderCaseWareStatementsStep()}
					{casewareMode && wizardStep === 3 && this.renderWizardStep3()}

					{manualMode && wizardStep === 2 && this.renderBankAccountReview()}
					{manualMode && wizardStep === 3 && this.renderWizardGlReference()}
					{manualMode && wizardStep === 4 && this.renderWizardStep3()}
					</React.Fragment>
					)}
				</div>

				{!periodGateOpen && (
				<div className="modal-footer bk-wizard__footer">
					<React.Fragment>
					{this.renderWizardBackButton(openingPhase)}
					{openingPhase && (
						<Button text="Skip — enter opening balances later" view="secondary" onClick={() => this.update({ openingBalancesDone: true })} />
					)}

					{glMode && wizardStep === 1 && glSourceChosen && !glOpeningReviewOpen && this.renderGlImportNextButton()}
					{glMode && wizardStep === 1 && glOpeningReviewOpen && (
						<Button text="Skip opening balances" view="secondary" onClick={this.onGlOpeningReviewDone} />
					)}
					{glMode && wizardStep === 2 && this.renderBankReviewContinueButton()}
					{glMode && wizardStep === 3 && this.renderGenerateButton()}

					{!openingPhase && casewareMode && wizardStep === 1 && !coaMappingOpen && (
						<Button
							text="Next"
							onClick={this.onStep1NextClicked}
							disabled={!canProceedStep1}
							title={!canProceedStep1 ? "Select CaseWare data with AM.dbf and BL.dbf first" : undefined}
						/>
					)}
					{casewareMode && wizardStep === 2 && this.renderCaseWareStatementsNextButton()}
					{casewareMode && wizardStep === 3 && this.renderGenerateButton()}

					{manualMode && wizardStep === 2 && this.renderBankReviewContinueButton()}
					{manualMode && wizardStep === 3 && this.renderGlReferenceNextButton()}
					{manualMode && wizardStep === 4 && this.renderGenerateButton()}
					</React.Fragment>
				</div>
				)}
			</Modal>
		);
	};

	renderStatementVerificationModal = () => {
		if (!this.state.statementVerificationGroups) return null;

		return (
			<StatementVerificationModal
				clientId={this.props.clientId}
				groups={this.state.statementVerificationGroups}
				accountsAStatementCouldBelongTo={this.listAccountsAStatementCouldBelongTo()}
				fiscalPeriod={this.state.fiscalPeriod}
				onComplete={this.onStatementVerificationComplete}
				onClose={this.onStatementVerificationClose}
			/>
		);
	};

	render() {
		const { loading, stillReadingTheBooks, transactions, industry, province, hasTax, cwFiles, bankAccountStatementMap, bankAccountsFromCw, sessionStartTime, cwPriorYearEnd, bookingPeriod, importedFileNames, clientIndustry } = this.state;
		const { clientId } = this.props;

		const cwCurrentYearEnd = UtilityService.addMonthsToIsoDate({ isoDate: cwPriorYearEnd, monthsToAdd: bookingPeriod });

		if (loading || stillReadingTheBooks) {
			return (
				<div className="container-loader">
					<Loader />
				</div>
			);
		}

		const statementFileNames = importedFileNames || statementFileNamesFromAccountMap(bankAccountsFromCw, bankAccountStatementMap);

		return (
			<div>
				{this.state.booksCouldNotBeRead && (
					<div className="bk-tool__load-failed">
						This client's books could not be read just now, so nothing is shown below. Reload the page rather
						than importing — importing again would write a second copy of transactions that are already on file.
					</div>
				)}
				{this.renderWizard()}
				{this.renderStatementVerificationModal()}

				<ToolsModal
					open={this.state.showClearConfirm}
					onClose={() => this.onClearConfirmClose({ confirmed: false })}
					title="Clear All Transactions?"
					width={480}
					footer={
						<>
							<span />
							<span className="tk-modal-actions">
								<button type="button" className="tk-button tk-button--secondary" onClick={() => this.onClearConfirmClose({ confirmed: false })}>No</button>
								<button type="button" className="tk-button tk-button--danger" onClick={() => this.onClearConfirmClose({ confirmed: true })}>Yes, clear all</button>
							</span>
						</>
					}
				>
					<div>
						<p className="bk-clear__intro">This will permanently delete:</p>
						<ul className="bk-clear__list">
							<li>All bank transactions</li>
							<li>All bank account records</li>
							<li>All adjusting entries (AJEs/RJEs)</li>
							<li>All Agent Mode decisions</li>
							<li>The AI's unapproved suggestions for this client, including what it found about each merchant</li>
						</ul>
						<p className="bk-clear__note">
							Your imported statements stay on the client's record. Generating again pulls
							the period's transactions back out of them, so nothing is uploaded or read a
							second time. Remove a statement deliberately from Imported Files when that is
							what you mean.
						</p>
						<p className="bk-clear__note">
							Also kept: the chart of accounts, the general ledger reference, prior-year
							figures, and this engagement's settings.
						</p>
					</div>
				</ToolsModal>


				{this.state.wpfEngagementId && !this.state.statementsWereChecked && (
					<div className="bk-tool__load-failed">
						Whether this file's statements reconcile could not be checked just now, so nothing is being held back
						below. Reload before relying on these books — a statement that does not add up would normally stop
						work here, and this page cannot currently tell you whether one does.
					</div>
				)}

				{this.renderAcceptStatementsModal()}

				{transactions.length > 0 && this.theBooksCannotBeBuiltOnYet() && this.renderStatementsMustBePutRight()}

				{transactions.length > 0 && !this.theBooksCannotBeBuiltOnYet() && (
					<div className="container">
						{this.thereAreStatementsToPutRight() && this.renderStatementDifferenceWasAccepted()}
						<BookkeepingTable
							clientId={clientId}
							wpfEngagementId={this.state.wpfEngagementId}
							transactions={transactions}
							getImportSourceFiles={this.getImportSourceFiles}
							onTransactionsUpdate={this.onTransactionsUpdate}
							initialIndustry={industry}
							initialProvince={province}
							initialHasTax={hasTax}
							gstHstAccount={this.state.gstHstAccount}
							initialCwFiles={cwFiles}
							initialGlReference={this.state.glReferenceForTable}
							initialGlEntries={this.state.loadedGlEntries}
							bankSideAccounts={this.state.loadedBankSideAccounts}
							cwCurrentYearEnd={cwCurrentYearEnd}
							onNewImport={this.onImportedFilesOpen}
							onClearTransactions={this.onClearTransactions}
							statementFileNames={statementFileNames}
							fiscalPeriod={this.state.fiscalPeriod}
							onFiscalPeriodChange={this.onFiscalPeriodChangedFromSettings}
							periodExclusions={this.state.periodExclusions}
							cwFileNames={cwFiles.map(f => f.name)}
							sessionStartTime={sessionStartTime}
							csrsChecklist={this.state.csrsChecklist}
							engagementState={this.state.engagementState}
							clientIndustry={clientIndustry}
						/>
					</div>
				)}

				{this.state.importedFilesModalOpen && (
					<ImportedFilesModal
						open
						clientId={clientId}
						wpfEngagementId={this.state.wpfEngagementId}
						isSignedOff={this.state.engagementState === "FINALIZED"}
						onClose={this.onImportedFilesClose}
						onAddFiles={this.onAddFilesOfKind}
						onFilesChanged={this.onImportedFilesChanged}
						onTransactionsChanged={this.onImportedFilesChanged}
						onPickUpUnreadStatements={this.onPickUpUnreadStatements}
					/>
				)}
			</div>
		);
	}
}

export default BookkeepingTool;
